/**
 * API Types - Request/Response interfaces for all endpoints
 * This file defines the contract between frontend and backend
 */

// ============ Auth Types ============

export enum UserRole {
  ADMIN = 'ADMIN',
  OFFICE_MANAGER = 'OFFICE_MANAGER',
  /** Deprecated in the backend schema (use OFFICE_MANAGER); legacy rows may still carry it. */
  OFFICE = 'OFFICE',
  TECHNICIAN = 'TECHNICIAN',
  CUSTOMER_PORTAL = 'CUSTOMER_PORTAL',
}

export interface User {
  id: string;
  email: string;
  /**
   * Not a backend field (the User model has no name column); AuthContext
   * derives it from the email. Kept for backward compatibility only.
   */
  name: string;
  role: UserRole;
  tenantId?: string;
  /**
   * True while the user holds a temporary password. The backend rejects every
   * protected route with 403 MUST_CHANGE_PASSWORD until it is cleared via
   * PATCH /users/me/password.
   */
  mustChangePassword: boolean;
  /**
   * MFA (tracker item 63): the company requires two-step verification for the
   * role and the user has not set it up. The backend answers 403
   * MFA_ENROLLMENT_REQUIRED on every staff route but the MFA ones until then.
   */
  mfaEnrollmentRequired?: boolean;
}

export interface LoginRequest {
  tenantSlug: string;
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  /** Includes mustChangePassword (backend auth.types.ts LoginResponse). */
  user: User & { tenantId: string };
}

export interface VerifyResponse {
  valid: boolean;
  /** The backend verify endpoint does not report mustChangePassword. */
  user?: Omit<User, 'mustChangePassword'>;
}

// ============ User Management Types ============
// Mirror backend users.types.ts. The DTOs never include the password hash.

/** GET /users list item (backend UserListResponse). */
export interface UserListItem {
  id: string;
  email: string;
  role: UserRole;
  isActive?: boolean;
  mustChangePassword: boolean;
  createdAt: string;
}

/**
 * GET /users/assignable row (backend AssignableUserResponse): an active
 * ADMIN, OFFICE_MANAGER or TECHNICIAN user a job can be assigned to. Carries
 * no account state; every row is active.
 */
export interface AssignableUser {
  id: string;
  email: string;
  role: UserRole;
}

/** GET/POST/PATCH /users single-user response (backend UserResponse). */
export interface UserDetail extends UserListItem {
  tenantId: string;
  updatedAt: string;
}

/** POST /users. `password` is temporary: the new user must change it on first login. */
export interface CreateUserRequest {
  email: string;
  password: string;
  role: UserRole;
}

/**
 * PATCH /users/:id. Send only the fields being changed. A `password` here is an
 * admin-initiated reset (sets mustChangePassword on the target).
 */
export interface UpdateUserRequest {
  email?: string;
  password?: string;
  role?: UserRole;
  isActive?: boolean;
}

/** POST /users/:id/reset-password. */
export interface ResetUserPasswordRequest {
  newPassword: string;
}

/** PATCH /users/me/password (self-service; clears the caller's mustChangePassword). */
export interface ChangeOwnPasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// ============ Customer Types ============

export type BillingMethod = 'PER_VISIT' | 'CONSOLIDATED';

/**
 * Sales-process label (display/filtering only; never blocks other actions).
 * LEAD/PROSPECT are pre-conversion; ACTIVE = being serviced; INACTIVE = churned
 * after being active; LOST = fell through before ever converting.
 */
export type CustomerStatus = 'LEAD' | 'PROSPECT' | 'ACTIVE' | 'INACTIVE' | 'LOST';

export interface CustomerSummary {
  // Money fields are serialized Decimals (strings)
  totalInvoiced: string;
  totalPaid: string;
  balanceDue: string;
  invoiceCount: number;
  propertyCount: number;
}

/**
 * Mirrors backend CustomerResponse (customer.service.ts mapToResponse).
 * Optional columns are nullable in Prisma, so they arrive as `null` in JSON.
 * The backend does not return tenantId, and does not currently populate
 * `summary` on either get or list.
 */
export interface Customer {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  taxExempt: boolean;
  billingMethod: BillingMethod;
  status: CustomerStatus;
  /** Current lead source; null when none. History: GET /customers/:id/lead-source-history. */
  leadSourceId?: string | null;
  /** No reminders or other non-transactional messages (tracker item 35) */
  notificationOptOut?: boolean;
  /** Set when an email to this address bounced or was reported as spam; cleared when the email changes */
  emailInvalid?: boolean;
  /** Excluded from auto-invoicing (tracker item 37) */
  autoInvoiceExempt?: boolean;
  /** Excluded from automated overdue reminders (tracker item 36) */
  dunningExempt?: boolean;
  /**
   * Default Dispatch route for this customer's Jobs (tracker item 57); null =
   * none. Changed on an existing customer only through setRouteDefault.
   */
  routeId?: string | null;
  createdAt: string;
  updatedAt: string;
  summary?: CustomerSummary;
}

export interface CreateCustomerRequest {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  taxExempt?: boolean;
  billingMethod?: BillingMethod;
  /** Backend defaults to LEAD when omitted. */
  status?: CustomerStatus;
  /** Setting a source records a lead source history row and an audit row. */
  leadSourceId?: string | null;
  /** Exclude from auto-invoicing (tracker item 37) */
  autoInvoiceExempt?: boolean;
  /** Exclude from automated overdue reminders (tracker item 36) */
  dunningExempt?: boolean;
  /** Optional default Dispatch route (an active route; tracker item 57). */
  routeId?: string | null;
}

/**
 * PATCH body. Optional text columns accept `null` to clear the stored value
 * (the backend passes the body through to Prisma, where these columns are nullable).
 */
export interface UpdateCustomerRequest {
  name?: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  taxExempt?: boolean;
  billingMethod?: BillingMethod;
  status?: CustomerStatus;
  /**
   * null clears the source. A value different from the current one records a
   * lead source history row and an audit row; the same value records nothing.
   */
  leadSourceId?: string | null;
  /** Notifications opt-out (reminders; invoices, statements and receipts still go out) */
  notificationOptOut?: boolean;
  /** Exclude from auto-invoicing (tracker item 37) */
  autoInvoiceExempt?: boolean;
  /** Exclude from automated overdue reminders (tracker item 36) */
  dunningExempt?: boolean;
}

// ============ Property Types ============

/** Mirrors backend property.types.ts PropertyResponse. */
export interface Property {
  id: string;
  /** Not part of the backend PropertyResponse; only populated by the mock client. */
  tenantId?: string;
  customerId: string;
  customer?: { id: string; name: string };
  billingCustomerId: string;
  billingCustomer?: { id: string; name: string };
  address: string;
  city: string;
  state: string;
  zip: string;
  taxRateId: string;
  taxRate?: { id: string; name: string; rate: string | number };
  /** Current lead source only (no history for properties). */
  leadSourceId?: string | null;
  /**
   * Property size as a Decimal(14,2) string, set together with sizeUnit (both
   * null when no size is set). Price charts read it. Optional only because
   * older mock rows may omit it; treat undefined like null.
   */
  sizeValue?: string | null;
  sizeUnit?: SizeUnit | null;
  /**
   * Default Dispatch route for Jobs here (tracker item 57); null = none.
   * Changed on an existing property only through setRouteDefault.
   */
  routeId?: string | null;
  /**
   * Geocoding (tracker item 41): decimal degrees (null when unknown), the
   * geocode status and precision. Office roles only: absent for technicians.
   */
  latitude?: number | null;
  longitude?: number | null;
  geocodeStatus?: GeocodeStatus;
  geocodePrecision?: GeocodePrecision | null;
  createdAt: string;
  updatedAt: string;
  recentJobs?: { count: number };
}

/**
 * Geocoding (tracker item 41). PENDING: waiting for the background geocoder;
 * OK: found (ADDRESS, or POSTCODE = approximate); NOT_FOUND; FAILED (retried
 * with backoff); MANUAL: set by hand; SKIPPED: geocoding is off on the server.
 */
export const GEOCODE_STATUSES = ['PENDING', 'OK', 'NOT_FOUND', 'FAILED', 'MANUAL', 'SKIPPED'] as const;
export type GeocodeStatus = (typeof GEOCODE_STATUSES)[number];
export type GeocodePrecision = 'ADDRESS' | 'POSTCODE';
export type DistanceUnit = 'MI' | 'KM';

/** PUT /properties/:id/location and POST /properties/:id/geocode response. */
export interface PropertyLocation {
  propertyId: string;
  latitude: number | null;
  longitude: number | null;
  geocodeStatus: GeocodeStatus;
  geocodePrecision: GeocodePrecision | null;
  geocodedAt: string | null;
  geocodeError: string | null;
}

/** GET /geocoding/status (ADMIN). */
export interface GeocodingStatus {
  /** nominatim, fake or none */
  provider: string;
  workerEnabled: boolean;
  properties: Record<GeocodeStatus, number> & { total: number };
  company: { geocodeStatus: GeocodeStatus; geocodePrecision: GeocodePrecision | null; hasAddress: boolean };
}

/** POST /geocoding/retry-failed (ADMIN). */
export interface RetryFailedGeocodingResult {
  requeued: number;
  companyRequeued: boolean;
}

/**
 * One invoice's contribution to a property balance (GET /properties/:id/balance,
 * tracker item 54). Amounts are 2-decimal strings.
 */
export interface PropertyBalanceInvoice {
  invoiceId: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  invoiceDate: string;
  dueDate: string;
  /** Invoice amount due (totalAmount + totalTax) */
  invoiceTotal: string;
  /** ACTIVE payment applications to the whole invoice */
  invoicePaid: string;
  /** This property's lines: lineAmount - lineDiscount + lineTax */
  propertyAmount: string;
  /** The property's pro rata share of invoicePaid (by propertyAmount / invoiceTotal, exact to the cent) */
  propertyPaid: string;
  /** propertyAmount - propertyPaid */
  propertyBalance: string;
  /** The invoice also bills other properties (or lines not tied to this one) */
  shared: boolean;
}

/**
 * A property's balance: its share of the POSTED / PARTIAL / PAID invoices that
 * bill its jobs (or its flat contract periods). DRAFT and VOID invoices never count.
 */
export interface PropertyBalance {
  propertyId: string;
  totalInvoiced: string;
  totalPaid: string;
  /** totalInvoiced - totalPaid */
  balanceDue: string;
  invoiceCount: number;
  /** Newest invoice first */
  invoices: PropertyBalanceInvoice[];
}

/** Backend requires every field except the optional lead source and size (property.business.ts validateCreateRequest). */
export interface CreatePropertyRequest {
  customerId: string;
  billingCustomerId: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  taxRateId: string;
  leadSourceId?: string | null;
  /** Both or neither: >= 0 with at most 2 decimal places, and a unit from SIZE_UNITS. */
  sizeValue?: string | number | null;
  sizeUnit?: SizeUnit | null;
  /** Optional default Dispatch route (an active route; tracker item 57). */
  routeId?: string | null;
}

/**
 * Backend PATCH whitelist: address, city, state, zip, billingCustomerId,
 * taxRateId, leadSourceId, sizeValue, sizeUnit.
 */
export interface UpdatePropertyRequest {
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  billingCustomerId?: string;
  taxRateId?: string;
  /** null clears the source. */
  leadSourceId?: string | null;
  /** The size after the update must be both-or-neither; null for both clears it. */
  sizeValue?: string | number | null;
  sizeUnit?: SizeUnit | null;
}

// ============ Price Chart Types ============
// Mirror backend price-charts/price-charts.types.ts.

/** Fixed list of size units (tenants cannot define units). */
export const SIZE_UNITS = ['SQFT', 'SQYD', 'SQM', 'ACRE', 'HECTARE', 'LINEAR_FT', 'METER'] as const;
export type SizeUnit = (typeof SIZE_UNITS)[number];

export type SizeDimension = 'AREA' | 'LENGTH';

export const PRICE_CHART_PRICING_TYPES = ['BAND_FLAT', 'RATE_PER_UNIT'] as const;
export type PriceChartPricingType = (typeof PRICE_CHART_PRICING_TYPES)[number];

/** GET /price-charts/units (backend SizeUnitResponse). */
export interface SizeUnitOption {
  unit: SizeUnit;
  dimension: SizeDimension;
  /** Display label, e.g. "sq ft". */
  label: string;
}

/** Backend PriceChartTierResponse. A band covers (fromSize, upToSize]. */
export interface PriceChartTier {
  id: string;
  sortOrder: number;
  /** Exclusive lower bound: the previous band's upToSize, "0" for the first band. */
  fromSize: string;
  /** Inclusive upper bound. */
  upToSize: string;
  /** Flat price (BAND_FLAT, and the first band of RATE_PER_UNIT) or per-unit rate. */
  price: string;
}

/** Backend PriceChartResponse. */
export interface PriceChart {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  unit: SizeUnit;
  dimension: SizeDimension;
  pricingType: PriceChartPricingType;
  /** Decimal(14,4) as a string. */
  overageRatePerUnit: string;
  active: boolean;
  tierCount: number;
  /** Items linked to this chart (soft-deleted items included). */
  itemCount: number;
  /** Job line items that were priced from this chart. */
  jobLineCount: number;
  /** True when deletion is blocked (itemCount or jobLineCount > 0). */
  inUse: boolean;
  /** Present on single-chart responses (get, create, update, PUT tiers); omitted from the list. */
  tiers?: PriceChartTier[];
  createdAt: string;
  updatedAt: string;
}

/** One band as sent on create / PUT tiers, in strictly increasing upToSize order. */
export interface PriceChartTierInput {
  upToSize: string | number;
  price: string | number;
}

export interface CreatePriceChartRequest {
  /** Unique per tenant (409 DUPLICATE_PRICE_CHART). */
  name: string;
  description?: string | null;
  unit: SizeUnit;
  pricingType: PriceChartPricingType;
  /** Required, >= 0, at most 4 decimal places. */
  overageRatePerUnit: string | number;
  /** Defaults to true. */
  active?: boolean;
  /** Optional initial tier set (same rules as PUT /:id/tiers). */
  tiers?: PriceChartTierInput[];
}

/** PATCH body. Omitted fields are unchanged; null clears description. Tiers go through PUT /:id/tiers. */
export interface UpdatePriceChartRequest {
  name?: string;
  description?: string | null;
  unit?: SizeUnit;
  pricingType?: PriceChartPricingType;
  overageRatePerUnit?: string | number;
  active?: boolean;
}

/** Where a quoted price comes from. Job lines add MANUAL. */
export type QuotePriceSource = 'CATALOG' | 'CHART';
export type JobPriceSource = QuotePriceSource | 'MANUAL';

/** POST /pricing/quote body. */
export interface PriceQuoteRequest {
  itemId: string;
  propertyId: string;
}

/**
 * POST /pricing/quote response (backend PriceQuoteResponse). CATALOG quotes
 * carry only unitPrice; CHART quotes add the chart, band, the property's size
 * as stored (size/unit) and that size in the chart's unit (chartSize/chartUnit).
 */
export interface PriceQuote {
  unitPrice: string;
  source: QuotePriceSource;
  chartId: string | null;
  tierId: string | null;
  size: string | null;
  unit: SizeUnit | null;
  chartSize: string | null;
  chartUnit: SizeUnit | null;
}

// ============ Item Types ============
// Mirror backend items/item.types.ts (ItemResponse, Create/UpdateItemRequest).

/** Backend ITEM_TYPES. */
export enum ItemType {
  SERVICE = 'SERVICE',
  LABOR = 'LABOR',
  PRODUCT = 'PRODUCT',
  FEE = 'FEE',
}

/** The GL account fields an item response embeds. */
export interface ItemGLAccountRef {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
}

/** GET /items, GET /items/:id (backend ItemResponse). Soft-deleted items are never returned. */
export interface Item {
  id: string;
  name: string;
  description?: string | null;
  itemType: ItemType;
  /** Decimal(14,2) as a string, e.g. "150.00". */
  unitPrice: string;
  /**
   * Per-unit cost as a Decimal(14,2) string, or null when not set. Drives cost
   * of goods sold when a PRODUCT item with an expense account is invoiced.
   */
  unitCost: string | null;
  unitOfMeasure: string;
  taxable: boolean;
  categoryId?: string | null;
  /** Omitted when the item has no category. */
  category?: { id: string; name: string };
  /** Must be a postable REVENUE account. */
  revenueAccount: ItemGLAccountRef;
  /** Postable EXPENSE account; omitted when not set. Required for PRODUCT items. */
  expenseAccount?: ItemGLAccountRef;
  /** Decimal string; maintained by inventory adjustments, read-only here. */
  quantityOnHand: string;
  /**
   * Low-stock threshold (PRODUCT items only) as a Decimal(14,2) string; null =
   * none. Optional only for older fixtures; treat undefined like null.
   */
  reorderPoint?: string | null;
  /**
   * Server-computed: reorderPoint is set and quantityOnHand <= reorderPoint.
   * Optional only for older fixtures; treat undefined like false.
   */
  lowStock?: boolean;
  /**
   * Typical minutes per 1 unit of quantity (SERVICE / LABOR items; tracker
   * item 40). A job's suggested duration sums these over its lines. null = none.
   */
  defaultDurationMinutes?: number | null;
  /**
   * When set, job lines for this item are priced from the chart at the
   * property's size; unitPrice stays the fallback when there is no chart.
   * Optional only for older fixtures; treat undefined like null.
   */
  priceChartId?: string | null;
  /** Default route for jobs created from this item. */
  routeId?: string | null;
  /**
   * Checklist template copied onto jobs the item is added to (tracker item 44).
   * null = none. Optional only for older fixtures; treat undefined like null.
   */
  checklistTemplateId?: string | null;
  /** Offered on the customer portal's request form (SERVICE / LABOR only; tracker item 45). Optional for older fixtures; treat undefined like false. */
  portalRequestable?: boolean;
  /**
   * System-owned item (e.g. the "Finance Charge" item on late-fee invoices).
   * The backend rejects editing or deleting it (409 SYSTEM_ITEM). Optional
   * for older fixtures; treat undefined like false.
   */
  isSystem?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateItemRequest {
  name: string;
  description?: string;
  itemType: ItemType;
  /** Non-negative; the column is Decimal(14,2). */
  unitPrice: string | number;
  /** Optional, non-negative, at most 2 decimal places. null / omitted means no cost. */
  unitCost?: string | number | null;
  /** Defaults to "each". */
  unitOfMeasure?: string;
  revenueAccountId: string;
  /** Required by the backend for PRODUCT items. */
  expenseAccountId?: string;
  categoryId?: string;
  /** Defaults to true. */
  taxable?: boolean;
  /** Must be an active chart in the tenant (422 PRICE_CHART_INACTIVE otherwise). */
  priceChartId?: string | null;
  /** Optional default dispatch route. */
  routeId?: string | null;
  /**
   * Must be an active checklist template in the tenant (404 NOT_FOUND when
   * missing, 422 CHECKLIST_TEMPLATE_INACTIVE when inactive). null / '' = none.
   */
  checklistTemplateId?: string | null;
  /** PRODUCT items only (400 otherwise); >= 0, at most 2 decimal places. null / omitted = none. */
  reorderPoint?: string | number | null;
  /** SERVICE / LABOR only (400 otherwise); whole minutes 5-1440. null / omitted = none. */
  defaultDurationMinutes?: number | null;
  /** Offer the item on the customer portal's request form. SERVICE / LABOR only (400 otherwise). */
  portalRequestable?: boolean;
}

/**
 * PATCH /items/:id. Omitted fields are left unchanged; null clears a nullable
 * field. Send only changed fields: the backend refuses any request carrying
 * revenueAccountId / expenseAccountId for an item that is in use, even when
 * the value is unchanged.
 */
export interface UpdateItemRequest {
  name?: string;
  description?: string | null;
  itemType?: ItemType;
  unitPrice?: string | number;
  unitCost?: string | number | null;
  unitOfMeasure?: string;
  revenueAccountId?: string;
  expenseAccountId?: string | null;
  categoryId?: string | null;
  taxable?: boolean;
  /** null unlinks the chart. A newly linked chart must be active. */
  priceChartId?: string | null;
  /** null clears the default dispatch route. */
  routeId?: string | null;
  /**
   * null / '' clears the checklist template link. Validated only when the link
   * changes (404 NOT_FOUND, 422 CHECKLIST_TEMPLATE_INACTIVE).
   */
  checklistTemplateId?: string | null;
  /**
   * null clears it. PRODUCT items only: changing a PRODUCT that has one to
   * another type must clear it in the same request (400 otherwise).
   */
  reorderPoint?: string | number | null;
  /** null clears it. SERVICE / LABOR items only (tracker item 40). */
  defaultDurationMinutes?: number | null;
  /** SERVICE / LABOR items only; changing the type to PRODUCT / FEE must send false in the same request. */
  portalRequestable?: boolean;
}

/** GET /item-categories (backend ItemCategoryResponse). */
export interface ItemCategory {
  id: string;
  name: string;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
  itemCount?: number;
}

// ============ Company Settings Types ============

/** Mirrors backend company-settings.types.ts CompanySettingsResponse. */
export interface CompanySettings {
  id: string;
  tenantId: string;
  companyName: string;
  logoUrl: string | null;
  taxId: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  invoiceFooterNote: string | null;
  defaultPaymentTermsDays: number;
  /** Decimal fraction per period, 4dp ("0.0150" = 1.5%) */
  financeChargeRate: string;
  financeChargeGraceDays: number;
  /** Money, 2dp */
  financeChargeMinAmount: string;
  /** How flat contract recurring schedules are billed (tracker item 34) */
  flatContractBillingMode: FlatContractBillingMode;
  /** Auto-invoicing (tracker item 37) */
  autoInvoicingMode: AutoInvoicingMode;
  /** YYYY-MM-DD: only jobs completed on or after it are auto-invoiced; null until first turned on */
  autoInvoicingStartDate: string | null;
  autoInvoicingAutoSend: boolean;
  /** Hour of the day (0-23, UTC) the nightly batch runs from */
  autoInvoicingNightlyHour: number;
  /** Automated dunning (tracker item 36): OFF or ON (absent on older responses = OFF) */
  dunningMode?: DunningMode;
  /** YYYY-MM-DD: only invoices due on or after it are reminded; null until first turned on */
  dunningStartDate?: string | null;
  /** Hour of the day (0-23, UTC) the daily reminder batch runs from */
  dunningHour?: number;
  /** Minimum days between two reminder emails to one customer */
  dunningMinDaysBetween?: number;
  /** Notifications master switch (set through Settings > Notifications) */
  notificationsEnabled?: boolean;
  notificationFromName?: string | null;
  /** Dispatch (tracker item 40): IANA timezone; job times and "today" are in it */
  timezone?: string;
  /** Job duration (minutes) when the lines suggest none */
  defaultJobDurationMinutes?: number;
  /** Default working days, 0 = Sunday .. 6 = Saturday */
  workDays?: number[];
  /** Default working hours, "HH:MM" */
  workdayStart?: string;
  workdayEnd?: string;
  /** Online payments (tracker item 38; set through Settings > Payments, read-only here) */
  onlinePaymentsEnabled?: boolean;
  onlinePaymentMinAmount?: string;
  autopayRetryDays?: number[];
  autopayMaxFailures?: number;
  /** Customer portal (tracker item 45; Settings > Customer portal). Absent on older responses. */
  portalEnabled?: boolean;
  /** 1-120: months an invoice's registration code stays usable */
  portalCodeValidMonths?: number;
  /** 1-20: portal logins one customer may have */
  portalMaxLoginsPerCustomer?: number;
  /** 0-720: hours of notice a reschedule request needs */
  portalRescheduleLeadHours?: number;
  /** Approve reschedule requests without a dispatch conflict automatically */
  portalRescheduleAutoApprove?: boolean;
  /** Conversation inbox (tracker item 56): STAFF (ADMIN + OFFICE_MANAGER) or EVERYONE (technicians too). Absent on older responses. */
  inboxAccess?: 'STAFF' | 'EVERYONE';
  /** Route optimization (tracker item 41): average speed in km/h (5-130). Absent on older responses. */
  routingSpeedKph?: number;
  /** Road distance factor over straight-line distance, "1.30" (1.00-3.00) */
  routingCircuityFactor?: string;
  /** Travel minutes assumed when a location is unknown (0-120) */
  routingUnknownTravelMinutes?: number;
  /** Display unit for distances and speeds (storage is km) */
  distanceUnit?: DistanceUnit;
  /** The company address's geocode (read-only; every technician's start point) */
  geocodeStatus?: GeocodeStatus;
  geocodePrecision?: GeocodePrecision | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * MANUAL: the office previews and bills due periods (Invoices > Bill flat
 * contracts). AUTOMATIC: the background recurring generator bills each
 * schedule's current period (when the server runs it).
 */
export const FLAT_CONTRACT_BILLING_MODES = ['MANUAL', 'AUTOMATIC'] as const;
export type FlatContractBillingMode = (typeof FLAT_CONTRACT_BILLING_MODES)[number];

/**
 * Auto-invoicing (tracker item 37). OFF: only the manual run on Invoices >
 * Auto-invoicing. ON_COMPLETION: per-visit jobs are invoiced when completed
 * (plus the nightly batch). NIGHTLY: one batch a day.
 */
export const AUTO_INVOICING_MODES = ['OFF', 'ON_COMPLETION', 'NIGHTLY'] as const;
export type AutoInvoicingMode = (typeof AUTO_INVOICING_MODES)[number];

/**
 * Automated dunning (tracker item 36). OFF: reminders only by hand on
 * Invoices > Dunning. ON: one batch a day from dunningHour (UTC).
 */
export const DUNNING_MODES = ['OFF', 'ON'] as const;
export type DunningMode = (typeof DUNNING_MODES)[number];

/**
 * PUT /company-settings body: companyName required; omitted fields are left
 * unchanged, null or '' clears a text field.
 */
export interface UpdateCompanySettingsRequest {
  companyName: string;
  logoUrl?: string | null;
  taxId?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  invoiceFooterNote?: string | null;
  defaultPaymentTermsDays?: number;
  financeChargeRate?: string;
  financeChargeGraceDays?: number;
  financeChargeMinAmount?: string;
  flatContractBillingMode?: FlatContractBillingMode;
  autoInvoicingMode?: AutoInvoicingMode;
  /** YYYY-MM-DD; null / '' clears it (only while OFF) */
  autoInvoicingStartDate?: string | null;
  autoInvoicingAutoSend?: boolean;
  autoInvoicingNightlyHour?: number;
  dunningMode?: DunningMode;
  /** YYYY-MM-DD; null / '' clears it (only while OFF) */
  dunningStartDate?: string | null;
  dunningHour?: number;
  dunningMinDaysBetween?: number;
  /** IANA timezone name (tracker item 40) */
  timezone?: string;
  /** 5-1440 */
  defaultJobDurationMinutes?: number;
  /** At least one of 0-6 */
  workDays?: number[];
  workdayStart?: string;
  workdayEnd?: string;
  /** Customer portal (tracker item 45) */
  portalEnabled?: boolean;
  portalCodeValidMonths?: number;
  portalMaxLoginsPerCustomer?: number;
  portalRescheduleLeadHours?: number;
  portalRescheduleAutoApprove?: boolean;
  /** Conversation inbox (tracker item 56) */
  inboxAccess?: 'STAFF' | 'EVERYONE';
  /** Routing (tracker item 41): km/h 5-130, factor 1.00-3.00, minutes 0-120, MI or KM */
  routingSpeedKph?: number;
  routingCircuityFactor?: string;
  routingUnknownTravelMinutes?: number;
  distanceUnit?: DistanceUnit;
}

// ============ Tax Types ============

/** Mirrors backend tax-rates.types.ts TaxRateResponse. */
export interface TaxRate {
  id: string;
  tenantId: string;
  name: string;
  /** Decimal fraction as a string, e.g. "0.0850" for 8.5%. */
  rate: string;
  payableAccount: {
    id: string;
    accountNumber: string;
    accountName: string;
  };
  /** How many properties use this rate; the backend refuses deletion when > 0. */
  propertyCount: number;
  createdAt: string;
}

export interface CreateTaxRateRequest {
  name: string;
  /** Decimal fraction between 0 and 1 (e.g. "0.085" for 8.5%). Stored as Decimal(5,4). */
  rate: string | number;
  /** GL account; must be a LIABILITY account with CREDIT normal balance. */
  payableAccountId: string;
}

export interface UpdateTaxRateRequest {
  name?: string;
  rate?: string | number;
  payableAccountId?: string;
}

// ============ Discount Types ============

export enum DiscountType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

/** Mirrors backend discounts.types.ts DiscountResponse. */
export interface Discount {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  type: DiscountType;
  /**
   * Decimal as a string. PERCENTAGE: whole percent 0-100 (e.g. "10" = 10%,
   * NOT a 0-1 fraction like tax rates). FIXED_AMOUNT: dollars (e.g. "25.5").
   */
  value: string;
  /** Contra-revenue GL account (REVENUE type). */
  discountAccount: {
    id: string;
    accountNumber: string;
    accountName: string;
  };
  active: boolean;
  /** YYYY-MM-DD, or null when open-ended. */
  startDate: string | null;
  /** YYYY-MM-DD, or null when open-ended. */
  endDate: string | null;
  /** Usage counts. itemCount is always 0 until items can take discounts. */
  customerCount: number;
  propertyCount: number;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiscountRequest {
  name: string;
  description?: string | null;
  type: DiscountType;
  /** PERCENTAGE: 0-100 inclusive. FIXED_AMOUNT: >= 0 with at most 2 decimal places. */
  value: string | number;
  /** GL account; must be a postable REVENUE account (e.g. "Sales Discounts"). */
  discountAccountId: string;
  /** Defaults to true. */
  active?: boolean;
  /** YYYY-MM-DD. */
  startDate?: string | null;
  /** YYYY-MM-DD; must not be before startDate. */
  endDate?: string | null;
}

/**
 * PATCH /discounts/:id body. Omitted fields are unchanged; `null` clears
 * description, startDate, or endDate.
 */
export interface UpdateDiscountRequest {
  name?: string;
  description?: string | null;
  type?: DiscountType;
  value?: string | number;
  discountAccountId?: string;
  active?: boolean;
  startDate?: string | null;
  endDate?: string | null;
}

// ============ Lead Source Types ============

export const UTM_FIELDS = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent'] as const;
export type UtmField = (typeof UTM_FIELDS)[number];

/** One value per UTM field; null when unset. */
export type UtmValues = Record<UtmField, string | null>;

/**
 * Mirrors backend lead-sources.types.ts LeadSourceResponse. GET /lead-sources
 * returns the tenant's whole tree as a flat list; build the hierarchy from
 * parentId (or use path/depth).
 */
export interface LeadSource extends UtmValues {
  id: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  description: string | null;
  active: boolean;
  /**
   * UTM values after inheritance: each blank field takes the nearest
   * ancestor's value. Computed live by the backend; never stored on the node.
   */
  effectiveUtm: UtmValues;
  /** Names from the root down, e.g. ["Online", "Google", "Google Ads"]. */
  path: string[];
  /** path joined with " > ". */
  pathLabel: string;
  /** 0 for top-level nodes. */
  depth: number;
  childCount: number;
  /** Reference counts (global by ID). Any non-zero count blocks deletion. */
  customerCount: number;
  customerHistoryCount: number;
  propertyCount: number;
  jobCount: number;
  inUse: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadSourceRequest {
  name: string;
  description?: string | null;
  /** null/omitted = top-level. */
  parentId?: string | null;
  /** Defaults to true. */
  active?: boolean;
  /** Blank/null UTM fields inherit from the parent. */
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
}

/**
 * PATCH /lead-sources/:id body. Omitted fields are unchanged; `null` clears.
 * Deactivating (active: true -> false) also deactivates every descendant;
 * `active: true` only reactivates this node. A parentId that would create a
 * cycle is rejected.
 */
export type UpdateLeadSourceRequest = Partial<CreateLeadSourceRequest>;

/** GET /customers/:id/lead-source-history item (newest first). */
export interface CustomerLeadSourceHistoryEntry {
  id: string;
  customerId: string;
  /** null when the source was cleared. */
  leadSourceId: string | null;
  leadSourceName: string | null;
  leadSourcePath: string | null;
  changedByUserId: string;
  /** null if the user no longer exists. */
  changedByEmail: string | null;
  changedAt: string;
}

// ============ Job Types ============

export enum JobStatus {
  DRAFT = 'DRAFT',
  WORK_ORDER_GENERATED = 'WORK_ORDER_GENERATED',
  SCHEDULED = 'SCHEDULED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  INVOICED = 'INVOICED',
  CANCELLED = 'CANCELLED',
}

export type JobPricingModel = 'FIXED' | 'HOURLY' | 'PER_ITEM';

export interface JobLineItem {
  id: string;
  itemId: string;
  item: Pick<Item, 'id' | 'name' | 'description' | 'itemType'>;
  quantity: string;
  unitPrice: string;
  pricingModel: JobPricingModel;
  /**
   * How unitPrice was set: CATALOG (item price), CHART (price chart at the
   * property's size) or MANUAL (entered). Optional only for older fixtures;
   * treat undefined like CATALOG.
   */
  priceSource?: JobPriceSource;
  /** CHART lines: the chart and the property's size when priced; otherwise null. */
  priceChartId?: string | null;
  sizeSnapshot?: string | null;
  sizeUnitSnapshot?: SizeUnit | null;
  taxRate: string;
  lineTotal: string;
  lineTax: string;
  lineAmount: string;
  createdAt: string;
}

export interface Job {
  id: string;
  /** Stored Dispatch route (tracker item 57); null = Unrouted. Optional for older mock data. */
  routeId?: string | null;
  /**
   * How routeId was set: DEFAULT (property, then customer default), ITEM (the
   * work items' route) or MANUAL (chosen by a person; never re-inferred).
   * Optional for older mock data; treat undefined like DEFAULT.
   */
  routeSource?: JobRouteSource;
  customerId: string;
  customer: Pick<Customer, 'id' | 'name'>;
  propertyId: string;
  property: Pick<Property, 'id' | 'address' | 'city' | 'state' | 'customerId'>;
  assignedTo: string | null;
  assignedToUser: Pick<User, 'id' | 'email'> | null;
  status: JobStatus;
  scheduledDate: string | null;
  completedDate?: string | null;
  notes?: string | null;
  lineItems: JobLineItem[];
  subtotal: string;
  totalTax: string;
  total: string;
  invoiceId?: string | null;
  leadSourceId: string | null;
  /**
   * Generated from a FLAT_CONTRACT recurring schedule: billed by the
   * contract's period invoices, never invoiced on its own (tracker item 34)
   */
  coveredByFlatContract?: boolean;
  /** Held back from auto-invoicing (tracker item 37) */
  autoInvoiceHold?: boolean;
  /** The job's current invoice was created by auto-invoicing (tracker item 37) */
  autoInvoiced?: boolean;
  /**
   * Dispatch (tracker item 40). Local "HH:MM" start in the company timezone
   * (null = all day / no time set).
   */
  scheduledStartTime?: string | null;
  /** Stored duration; null only on jobs not edited since item 40 (use suggestedDurationMinutes) */
  estimatedDurationMinutes?: number | null;
  /** Computed from the current lines (company fallback when they give 0) */
  suggestedDurationMinutes?: number;
  /** AUTO: follows the lines; MANUAL: set by a person */
  durationSource?: JobDurationSource;
  /** Customer-facing arrival window in minutes */
  arrivalWindowMinutes?: number | null;
  /**
   * Checklist progress (tracker item 44). Optional for older mock data;
   * treat undefined like no steps.
   */
  checklist?: JobChecklistProgress;
  /**
   * Service bundles (tracker item 85): the enrollment this visit belongs to,
   * its 1-based step, the step count and the bundle name; null outside an
   * enrollment. Optional for older mock data.
   */
  enrollmentId?: string | null;
  enrollmentStep?: number | null;
  enrollmentStepCount?: number | null;
  bundleName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type JobDurationSource = 'AUTO' | 'MANUAL';

export type JobRouteSource = 'DEFAULT' | 'ITEM' | 'MANUAL';

export interface CreateJobRequest {
  propertyId: string;
  assignedTo?: string | null;
  scheduledDate?: string | null;
  notes?: string;
  leadSourceId?: string | null;
  /** "HH:MM"; needs a scheduledDate (tracker item 40) */
  scheduledStartTime?: string | null;
  /** Given = MANUAL; omitted = AUTO (suggested from the lines) */
  estimatedDurationMinutes?: number | null;
  arrivalWindowMinutes?: number | null;
  /**
   * Sending it (even null = Unrouted) makes the route MANUAL: it no longer
   * follows the work items or defaults. Omit it to keep automatic inference.
   */
  routeId?: string | null;
}

export interface UpdateJobRequest extends Partial<CreateJobRequest> {
  /** INVOICED is set only by invoicing. */
  status?: Exclude<JobStatus, JobStatus.INVOICED>;
  /** Hold back from auto-invoicing (office roles; tracker item 37) */
  autoInvoiceHold?: boolean;
  /** 'AUTO' = reset the duration to the suggestion from the lines (tracker item 40) */
  durationSource?: JobDurationSource;
  /** Bundle visit with a new scheduledDate: also move the later SCHEDULED visits by the same days (tracker item 85) */
  shiftLaterVisits?: boolean;
}

export interface AddJobLineItemRequest {
  itemId: string;
  quantity: string | number;
  /**
   * Present (including 0) = MANUAL price. Omitted = priced automatically:
   * from the item's price chart at the property's size (CHART), else the
   * item's unit price (CATALOG).
   */
  unitPrice?: string | number;
  pricingModel?: JobPricingModel;
  /**
   * Required when the request changes the unit price of a bundle visit's line
   * (tracker item 85): without it the server answers 400
   * RENEWAL_PRICE_CHOICE_REQUIRED and saves nothing.
   */
  renewalPriceAction?: 'UPDATE' | 'KEEP';
}

/**
 * Omitted fields are unchanged. A unitPrice makes the line MANUAL; changing
 * the item without a unitPrice re-prices it; changing only quantity or
 * pricing model keeps the stored price and source.
 */
export type UpdateJobLineItemRequest = Partial<AddJobLineItemRequest>;

export interface JobListFilters {
  status?: JobStatus;
  customerId?: string;
  propertyId?: string;
  assignedTo?: string;
  search?: string;
  /** Scheduled date range, inclusive (YYYY-MM-DD; tracker item 40) */
  scheduledFrom?: string;
  scheduledTo?: string;
}

// ============ Dispatch Types (tracker item 40) ============

export type ScheduleConflictType = 'OVERLAP' | 'OUTSIDE_WORKING_HOURS' | 'TIME_OFF' | 'PAST_DATE';

export interface ScheduleConflict {
  type: ScheduleConflictType;
  message: string;
  /** OVERLAP: the clashing jobs */
  jobIds?: string[];
}

export interface WorkHours {
  /** 0 = Sunday .. 6 = Saturday */
  workDays: number[];
  startTime: string;
  endTime: string;
}

export interface EffectiveWorkHours extends WorkHours {
  source: 'COMPANY' | 'TECHNICIAN';
}

export interface BoardJob {
  id: string;
  /** Stored Dispatch route (tracker item 57); null = Unrouted. Optional for older mock data. */
  routeId?: string | null;
  status: JobStatus | string;
  customer: { id: string; name: string };
  property: { id: string; address: string; city: string; state: string };
  assignedTo: string | null;
  assignedToEmail: string | null;
  scheduledDate: string | null;
  scheduledStartTime: string | null;
  /** Effective duration (stored, else suggested) */
  durationMinutes: number;
  estimatedDurationMinutes: number | null;
  suggestedDurationMinutes: number;
  durationSource: JobDurationSource | string;
  arrivalWindowMinutes: number | null;
  notes: string | null;
  lineSummary: string;
  lineCount: number;
  /** false for COMPLETED / INVOICED / CANCELLED: read-only on the board */
  schedulable: boolean;
  /** Send back as expectedUpdatedAt */
  updatedAt: string;
  conflicts: ScheduleConflict[];
  /**
   * The property's stored coordinates (tracker item 41, phase 4); null when
   * it has none yet (the card shows "location unknown"). Optional for older
   * mock data.
   */
  location?: BoardLocation | null;
  /** Service bundles (tracker item 85): the visit's enrollment chip data; null outside an enrollment */
  enrollmentId?: string | null;
  enrollmentStep?: number | null;
  enrollmentStepCount?: number | null;
  bundleName?: string | null;
}

export interface BoardLocation {
  lat: number;
  lng: number;
  /** ADDRESS, POSTCODE (approximate), or null for a hand-set pin */
  precision: string | null;
}

/** One drive on a technician's day (tracker item 41, phase 4). */
export interface BoardTravelLeg {
  /** null = from the company address (the first leg of the day) */
  fromJobId: string | null;
  toJobId: string;
  minutes: number;
  /** Straight-line km; null when either end has no coordinates */
  km: number | null;
  /** True when either end has no coordinates (minutes is the company estimate) */
  unknown: boolean;
}

/** The drives of one technician's day: timed jobs except cancelled ones, by start time. */
export interface BoardTravelDay {
  userId: string;
  date: string;
  legs: BoardTravelLeg[];
}

export interface BoardTechnician {
  id: string;
  email: string;
  role: UserRole | string;
  workHours: EffectiveWorkHours;
  /** Usual routes (tracker item 57; informational only) */
  usualRouteIds?: string[];
  /** Extra dated coverage on the board's selected date only; week boards use DispatchBoard.routeAssignments */
  dayRouteIds?: string[];
}

export interface TimeOff {
  id: string;
  userId: string;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  reason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export type BoardView = 'day' | 'week';

export interface DispatchBoard {
  view: BoardView;
  date: string;
  from: string;
  to: string;
  /** Today / now (minutes after midnight) in the company timezone */
  today: string;
  nowMinutes: number;
  timezone: string;
  companyWorkHours: WorkHours;
  technicians: BoardTechnician[];
  /** Every tenant route, active or not (tracker item 57) */
  routes?: DispatchRoute[];
  /** Dated technician coverage across the board's range */
  routeAssignments?: RouteAssignment[];
  jobs: BoardJob[];
  unassigned: BoardJob[];
  timeOff: TimeOff[];
  truncated: boolean;
  cap: number;
  /** Drives between consecutive timed jobs per technician and day (tracker item 41, phase 4) */
  travel?: BoardTravelDay[];
  /** Display unit for distances */
  distanceUnit?: DistanceUnit | string;
  /** Whether the company address (every day's start) has coordinates */
  depotKnown?: boolean;
}

export interface DispatchBacklog {
  data: BoardJob[];
  nextCursor: string | null;
}

/** Schedule fields: omitted = unchanged, null clears (duration null = back to the suggestion). */
export interface ScheduleChange {
  scheduledDate?: string | null;
  scheduledStartTime?: string | null;
  estimatedDurationMinutes?: number | null;
  arrivalWindowMinutes?: number | null;
  assignedTo?: string | null;
}

export interface ScheduleJobRequest extends ScheduleChange {
  /** The job's updatedAt as last read; a newer one is 409 JOB_CHANGED */
  expectedUpdatedAt: string;
  /** Applying a suggestion (tracker item 41): any conflict is 409 SUGGESTION_STALE and nothing is written */
  requireNoConflicts?: boolean;
  /** Where the slot came from; stored on the job.schedule audit row */
  origin?: ScheduleOrigin;
}

/*
 * Route suggestions (backend POST /dispatch/suggestions, tracker item 41 phase 2).
 */

export type SuggestionReasonCode =
  | 'ROUTE_MATCH'
  | 'ROUTE_MISMATCH'
  | 'NEAR_JOB'
  | 'FROM_DEPOT'
  | 'LIGHT_DAY'
  | 'BUSY_DAY'
  | 'EARLIEST_AVAILABLE'
  | 'APPROXIMATE_LOCATION'
  | 'LOCATION_UNKNOWN';

export interface SuggestionReason {
  code: SuggestionReasonCode;
  params: Record<string, string | number | boolean | null>;
}

export interface ScheduleOrigin {
  kind: 'AUTO_ASSIGN';
  score: number;
  reasons: SuggestionReason[];
}

export interface SuggestionRequest {
  jobId: string;
  from?: string;
  to?: string;
  technicianIds?: string[];
  /** Also try other technicians than the current assignee */
  anyTechnician?: boolean;
  /** Also try other dates than the job's current date */
  anyDate?: boolean;
  /** 1 to 10, default 5 */
  limit?: number;
}

export interface SuggestionCandidate {
  technicianId: string;
  technicianEmail: string;
  date: string;
  startTime: string;
  endTime: string;
  /** Lower is better */
  score: number;
  scoreBreakdown: { addedTravel: number; route: number; dayLoad: number; daysOut: number; unknownLocation: number };
  travelInMinutes: number;
  travelOutMinutes: number | null;
  addedTravelMinutes: number;
  distanceUnit: DistanceUnit | string;
  reasons: SuggestionReason[];
}

export interface SuggestionExcluded {
  notWorking: number;
  timeOff: number;
  pastDate: number;
  noGapFits: number;
  unavailable: number;
  conflicts: number;
}

export interface SuggestionResult {
  job: { id: string; durationMinutes: number; location: { lat: number; lng: number; precision: string | null } | null };
  from: string;
  to: string;
  candidates: SuggestionCandidate[];
  excluded: SuggestionExcluded;
  truncated: boolean;
}

/*
 * Bulk preview for the visible backlog (backend POST
 * /dispatch/suggestions/bulk, tracker item 41, phase 4). Writes nothing;
 * each placement is applied through scheduleJob with requireNoConflicts.
 */
export interface BulkSuggestionRequest {
  /** 1 to 25 job ids; placed in backlog order (created, then id) */
  jobIds: string[];
  from?: string;
  to?: string;
  anyTechnician?: boolean;
  anyDate?: boolean;
}

export type BulkUnplacedReason = 'NO_SLOT' | 'OUTSIDE_HORIZON' | 'NOT_SCHEDULABLE';

export interface BulkPlacement {
  jobId: string;
  customerName: string;
  address: string;
  durationMinutes: number;
  /** Send back as expectedUpdatedAt */
  expectedUpdatedAt: string;
  candidate: SuggestionCandidate | null;
  reason: BulkUnplacedReason | null;
  excluded: SuggestionExcluded | null;
}

export interface BulkSuggestionResult {
  from: string;
  to: string;
  placements: BulkPlacement[];
  placed: number;
  unplaced: number;
  truncated: boolean;
}

export interface ScheduleJobResult {
  job: Job;
  conflicts: ScheduleConflict[];
}

/*
 * Optimize one technician's day (backend POST /dispatch/optimize-day and
 * /dispatch/optimize-day/apply, tracker item 41 phase 3).
 */

/** Why a stop keeps its time: in progress, finished, "keep time" ticked, or already past (today). */
export type OptimizerPinReason = 'IN_PROGRESS' | 'DONE' | 'KEPT' | 'PASSED';
export type OptimizerOutcome = 'IMPROVED' | 'ALREADY_BEST' | 'NO_FEASIBLE_ORDER' | 'NOTHING_TO_MOVE' | 'DAY_OFF';

export interface OptimizeDayRequest {
  userId: string;
  date: string;
  /** Jobs whose time the office keeps ("keep time") */
  pinnedJobIds?: string[];
}

export interface OptimizeDayJob {
  jobId: string;
  customerName: string;
  address: string;
  status: string;
  pinned: boolean;
  pinReason: OptimizerPinReason | null;
  /** 1-based positions in the current and the proposed order */
  oldPosition: number;
  newPosition: number;
  oldStartTime: string;
  newStartTime: string;
  newEndTime: string;
  durationMinutes: number;
  travelInMinutes: number;
  travelInUnknown: boolean;
  travelInDistance: number | null;
  arrivalWindow: { earliest: string; latest: string } | null;
  locationKnown: boolean;
  expectedUpdatedAt: string;
  changed: boolean;
}

export interface OptimizeDayPlan {
  order: string[];
  totalTravelMinutes: number;
  dayEnd: string | null;
}

export interface OptimizeDayChange {
  jobId: string;
  expectedUpdatedAt: string;
  scheduledStartTime: string;
}

export interface OptimizeDayResult {
  userId: string;
  technicianEmail: string;
  date: string;
  workHours: { startTime: string; endTime: string };
  outcome: OptimizerOutcome;
  improved: boolean;
  algorithm: 'EXACT' | 'HEURISTIC' | null;
  movableCount: number;
  current: OptimizeDayPlan;
  proposed: OptimizeDayPlan;
  minutesSaved: number;
  /** Every timed stop, in the proposed order */
  jobs: OptimizeDayJob[];
  leftOut: { jobId: string; customerName: string; reason: 'NO_TIME' }[];
  /** What Apply sends */
  changes: OptimizeDayChange[];
  distanceUnit: DistanceUnit | string;
  depotKnown: boolean;
  unknownLocations: number;
  conflicts: { jobId: string; conflicts: ScheduleConflict[] }[];
}

export interface ApplyDayOptimizationRequest {
  userId: string;
  date: string;
  changes: OptimizeDayChange[];
}

export interface ApplyDayOptimizationResult {
  runId: string;
  userId: string;
  date: string;
  applied: number;
  jobIds: string[];
}

export interface TechnicianWorkHours extends WorkHours {
  userId: string;
  source: 'COMPANY' | 'TECHNICIAN';
  companyDefault: WorkHours;
}

export interface CreateTimeOffRequest {
  userId: string;
  startDate: string;
  endDate?: string;
  startTime?: string | null;
  endTime?: string | null;
  reason?: string | null;
}

export type UpdateTimeOffRequest = Partial<Omit<CreateTimeOffRequest, 'userId'>>;

export interface TimeOffQuery {
  from?: string;
  to?: string;
  userId?: string;
}

/** Dispatch route configured for a tenant (tracker item 57). */
export interface DispatchRoute {
  id: string;
  name: string;
  isActive: boolean;
}

export interface RouteAssignment {
  id: string;
  userId: string;
  routeId: string;
  date: string;
}

export interface RouteAssignmentQuery {
  date: string;
  userId?: string;
}

export interface CreateDispatchRouteRequest { name: string; }
export interface UpdateDispatchRouteRequest { name?: string; isActive?: boolean; }
export interface CreateRouteAssignmentRequest { userId: string; routeId: string; date: string; }

/**
 * What a changed Customer / Property default does to existing Jobs in scope
 * (never COMPLETED / INVOICED / CANCELLED ones): NONE leaves them, ALL sets
 * every one (manual routes included), MATCH_OLD only those whose route equals
 * the former default. Item defaults never rewrite Jobs (NONE only).
 */
export type RoutePropagation = 'NONE' | 'ALL' | 'MATCH_OLD';
export type RouteDefaultEntity = 'customer' | 'property' | 'item';

export interface SetRouteDefaultRequest {
  routeId: string | null;
  routePropagation?: RoutePropagation;
}

export interface SetRouteDefaultResult {
  entityId: string;
  routeId: string | null;
  /** Jobs whose route was changed */
  updatedJobs: number;
}

// ============ Invoice Types ============

export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  POSTED = 'POSTED',
  PARTIAL = 'PARTIAL',
  PAID = 'PAID',
  VOID = 'VOID',
}

/**
 * STANDARD: billed from jobs. FINANCE_CHARGE: a late fee created by the
 * finance-charge run (one untaxed line, no jobs). Absent on older responses,
 * which means STANDARD.
 */
export type InvoiceType = 'STANDARD' | 'FINANCE_CHARGE';

export interface InvoiceLineItem {
  id: string;
  jobLineItemId?: string | null;
  itemId: string;
  item: { id: string; name: string; itemType: string };
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  /** Gross: quantity x unitPrice, before discounts and tax */
  lineAmount: string;
  /** This line's share of the invoice's discounts (absent on old responses) */
  lineDiscount?: string;
  lineTax: string;
  lineCogs: string;
  createdAt: string;
}

export type InvoiceDiscountType = 'PERCENTAGE' | 'FIXED_AMOUNT';

/** A discount applied to an invoice (snapshot taken at creation). */
export interface InvoiceDiscount {
  id: string;
  /** The catalog discount; null once deleted */
  discountId: string | null;
  name: string;
  type: InvoiceDiscountType;
  value: string;
  amount: string;
  discountAccountId: string;
}

export interface Invoice {
  id: string;
  tenantId: string;
  billingCustomerId: string;
  billingCustomer: { id: string; name: string; email: string | null };
  invoiceNumber: string;
  status: InvoiceStatus;
  /** Absent on older responses (= STANDARD) */
  invoiceType?: InvoiceType;
  /** Automated dunning (tracker item 36): overdue reminders on hold */
  dunningHold?: boolean;
  invoiceDate: string;
  dueDate: string;
  periodStartDate?: string;
  periodEndDate?: string;
  billingMethod: BillingMethod;
  lineItems: InvoiceLineItem[];
  jobs: Array<{ id: string; status: string }>;
  /** Gross before discounts and tax (totalAmount + totalDiscount) */
  subtotal?: string;
  /** Sum of the applied discounts */
  totalDiscount?: string;
  discounts?: InvoiceDiscount[];
  /** Net before tax (after discounts) */
  totalAmount: string;
  totalTax: string;
  totalCogs: string;
  /** Amount due: totalAmount + totalTax */
  total: string;
  /** Customer portal registration code "XXXX-XXXX" (tracker item 45); null on a DRAFT or VOID invoice, absent on older responses */
  portalCode?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInvoiceRequest {
  billingCustomerId: string;
  jobIds: string[];
  invoiceDate: string;
  /** Optional: the backend defaults it to invoiceDate + the company's payment terms. */
  dueDate?: string;
  billingMethod: BillingMethod;
  periodStartDate?: string;
  periodEndDate?: string;
  /**
   * Omitted: the backend applies every applicable discount. A list (even
   * empty) applies exactly those.
   */
  discountIds?: string[];
}

/** POST /invoices/preview: the invoice as the server would create it. */
export interface InvoicePreview {
  billingCustomerId: string;
  billingMethod: string;
  invoiceDate: string;
  lineItems: Array<{
    jobId: string;
    jobLineItemId: string;
    itemId: string;
    description: string;
    quantity: string;
    unitPrice: string;
    taxRate: string;
    lineAmount: string;
    lineDiscount: string;
    lineTax: string;
  }>;
  /** Every discount applicable today; `selected` = part of this preview */
  applicableDiscounts: Array<{ discountId: string; name: string; type: InvoiceDiscountType; value: string; selected: boolean }>;
  appliedDiscounts: Array<{ discountId: string; name: string; type: InvoiceDiscountType; value: string; amount: string; discountAccountId: string }>;
  subtotal: string;
  totalDiscount: string;
  totalAmount: string;
  totalTax: string;
  total: string;
}

export interface UpdateInvoiceRequest {
  dueDate?: string;
}

export interface InvoiceListFilters {
  status?: InvoiceStatus;
  billingCustomerId?: string;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
}

/** POST /invoices returns data: { data: invoice, glPosting, jobsUpdated }. */
export interface InvoiceCreationResult {
  data: Invoice;
  glPosting: { invoiceId: string; journalEntryId: string; lineCount: number; totalDebits: string; totalCredits: string };
  jobsUpdated: number;
}

// ============ Payment Types ============

export enum PaymentMethod {
  CASH = 'CASH',
  CHECK = 'CHECK',
  CC = 'CC',
  BANK_TRANSFER = 'BANK_TRANSFER',
}

export type PaymentStatus = 'ACTIVE' | 'VOIDED';

export interface PaymentApplication {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  amountApplied: string;
  invoiceTotal: string;
  invoiceStatus: InvoiceStatus;
  /** PAYMENT (allocated when recorded) or CREDIT (existing credit applied later, tracker item 55); absent on older responses */
  source?: PaymentApplicationSource;
  /** YYYY-MM-DD the application counts from */
  appliedOn?: string;
  createdAt: string;
}

export type PaymentApplicationSource = 'PAYMENT' | 'CREDIT';

export interface Payment {
  id: string;
  tenantId: string;
  billingCustomerId: string;
  billingCustomer: Pick<Customer, 'id' | 'name' | 'email'>;
  amount: string;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  referenceNumber?: string;
  memo?: string;
  status: PaymentStatus;
  voidedAt?: string;
  voidReason?: string;
  /** Online payments (tracker item 38): set when a card processor took the payment, else null (absent on older responses) */
  processor?: string | null;
  processorPaymentId?: string | null;
  processorStatus?: ProcessorPaymentStatus | null;
  processingFee?: string | null;
  receiptUrl?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  applications: PaymentApplication[];
  unappliedAmount: string;
  createdAt: string;
}

/** COMPLETED, REFUND_PENDING, REFUNDED (voided too unless the void is still pending), REFUNDED_EXTERNALLY (refunded in the processor dashboard; void pending). */
export type ProcessorPaymentStatus = 'COMPLETED' | 'REFUND_PENDING' | 'REFUNDED' | 'REFUNDED_EXTERNALLY';

export interface RecordPaymentRequest {
  billingCustomerId: string;
  amount: string | number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  referenceNumber?: string;
  memo?: string;
  /** Omit for FIFO allocation. Provided IDs are applied in the given order. */
  invoiceIds?: string[];
}

export interface PaymentListFilters {
  billingCustomerId?: string;
  invoiceId?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  status?: PaymentStatus;
}

export interface PaymentCreationResult {
  data: Payment;
  glPosting: { paymentId: string; journalEntryId: string; lineCount: number; totalDebits: string; totalCredits: string };
  invoicesUpdated: number;
}

export interface PaymentGLEntry {
  paymentId: string;
  journalEntry: { id: string; entryDate: string; memo?: string; sourceType: string; sourceId: string; createdBy: string; createdAt: string };
  lines: Array<{ id: string; glAccount: { accountNumber: string; accountName: string; accountType: string }; debit: string; credit: string; customerId?: string }>;
}

// Apply existing customer credit (tracker item 55). No GL entry is posted.

export interface ApplyCreditRequest {
  invoiceId: string;
  /** Omitted: the smaller of the payment's credit and the invoice balance */
  amount?: string;
}

export interface CreditInvoiceSummary {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  total: string;
  remainingBalance: string;
}

export interface ApplyCreditResult {
  payment: Payment;
  application: PaymentApplication;
  invoice: CreditInvoiceSummary;
}

export interface UnapplyCreditResult {
  payment: Payment;
  invoice: CreditInvoiceSummary;
}

export interface CustomerCreditPayment {
  paymentId: string;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string | null;
  amount: string;
  unappliedAmount: string;
  /** false while a card refund of the payment is pending or done */
  applicable: boolean;
}

export interface CustomerCredits {
  customerId: string;
  totalUnapplied: string;
  payments: CustomerCreditPayment[];
}

export interface CreditTargetInvoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  status: InvoiceStatus;
  total: string;
  remainingBalance: string;
}

export interface CreditTargets {
  paymentId: string;
  billingCustomerId: string;
  canApply: boolean;
  unappliedAmount: string;
  invoices: CreditTargetInvoice[];
}

// ============ GL Types ============

export enum GLAccountType {
  ASSET = 'ASSET',
  LIABILITY = 'LIABILITY',
  EQUITY = 'EQUITY',
  REVENUE = 'REVENUE',
  EXPENSE = 'EXPENSE',
}

/** Mirrors backend gl.types.ts GLAccountResponse. */
export interface GLAccount {
  id: string;
  tenantId: string;
  accountNumber: string;
  accountName: string;
  accountType: GLAccountType;
  parentId?: string;
  normalBalance: 'DEBIT' | 'CREDIT';
  isPostable: boolean;
  /**
   * True once any journal line has been posted to the account. The backend then
   * rejects changes to accountNumber, accountType and normalBalance.
   */
  hasJournalLines: boolean;
  createdAt: string;
}

export interface CreateGLAccountRequest {
  accountNumber: string;
  accountName: string;
  accountType: GLAccountType;
  normalBalance: 'DEBIT' | 'CREDIT';
  parentId?: string;
  isPostable?: boolean;
}

/**
 * PATCH /gl/accounts/:id body. Omitted fields are unchanged; `parentId: null`
 * clears the parent.
 */
export interface UpdateGLAccountRequest {
  accountNumber?: string;
  accountName?: string;
  accountType?: GLAccountType;
  normalBalance?: 'DEBIT' | 'CREDIT';
  parentId?: string | null;
  isPostable?: boolean;
}

/** POST /gl/seed response. */
export interface SeedChartOfAccountsResponse {
  count: number;
  accounts: GLAccount[];
}

/** Mirrors backend gl.types.ts JOURNAL_SOURCE_TYPES. There is no MANUAL type: manual entries post as ADJUSTMENT. */
export const JOURNAL_SOURCE_TYPES = ['INVOICE', 'PAYMENT', 'ADJUSTMENT', 'FINANCE_CHARGE'] as const;
export type JournalSourceType = (typeof JOURNAL_SOURCE_TYPES)[number];

/**
 * Mirrors backend JournalLineDetailResponse. Amounts are decimal strings
 * ("100", "12.34"); exactly one of debit / credit is non-zero.
 */
export interface JournalEntryLine {
  id: string;
  journalEntryId: string;
  glAccountId: string;
  accountNumber: string;
  accountName: string;
  accountType: GLAccountType;
  debit: string;
  credit: string;
  /** AR subledger tag; omitted when empty */
  customerId?: string;
}

/**
 * Mirrors backend JournalEntryDetailResponse (GET/POST /gl/journal-entries,
 * GET /gl/journal-entries/:id, POST /gl/journal-entries/:id/reverse).
 */
export interface JournalEntry {
  id: string;
  tenantId: string;
  /** YYYY-MM-DD */
  entryDate: string;
  memo?: string;
  sourceType: JournalSourceType;
  sourceId: string;
  createdBy: string;
  /** Creating user's email, or null if the user row is gone */
  createdByEmail: string | null;
  createdAt: string;
  /** The ADJUSTMENT entry that reverses this one, or null */
  reversedByEntryId: string | null;
  /** For a reversal: the entry it reverses, otherwise null */
  reversesEntryId: string | null;
  lines: JournalEntryLine[];
}

/** POST /gl/journal-entries body. Amounts are decimal strings; the unused side is "0". */
export interface CreateJournalEntryRequest {
  entryDate: string;
  memo?: string;
  sourceType: JournalSourceType;
  sourceId: string;
  lines: Array<{
    glAccountId: string;
    debit: string;
    credit: string;
    customerId?: string;
  }>;
}

/** GET /gl/journal-entries filters (all optional, AND-ed; blank values are not sent). */
export interface JournalEntryListFilters {
  /** Inclusive YYYY-MM-DD on entryDate */
  startDate?: string;
  /** Inclusive YYYY-MM-DD on entryDate */
  endDate?: string;
  sourceType?: JournalSourceType | '';
  /** Entries with at least one line on this account */
  accountId?: string;
}

// ============ Report Types ============

// Mirrors backend/src/modules/reports/report.types.ts. Money values are
// decimal strings from the server; display them, never sum them client-side.

export interface ReportDateRange {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
}

export interface ReportPagination {
  page?: number;
  limit?: number;
}

export interface TrialBalanceQuery {
  asOfDate?: string; // YYYY-MM-DD; server defaults to today
}

export interface TrialBalanceAccountRow {
  glAccountId: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  normalBalance: string;
  debit: string;
  credit: string;
  balance: string; // signed per normalBalance convention
}

export interface TrialBalanceReport {
  asOfDate: string;
  accounts: TrialBalanceAccountRow[];
  totalDebits: string;
  totalCredits: string;
  balanced: boolean;
}

export interface ARAgingQuery {
  asOfDate?: string; // YYYY-MM-DD; server defaults to today
}

export interface ARAgingBuckets {
  current: string;
  days1to30: string;
  days31to60: string;
  days61to90: string;
  days90plus: string;
}

export type ARAgingBucket = 'current' | '1-30' | '31-60' | '61-90' | '90+';

export interface ARAgingInvoiceRow {
  invoiceId: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  daysOverdue: number;
  bucket: ARAgingBucket;
  remainingBalance: string;
  /** STANDARD or FINANCE_CHARGE (a late fee); absent on older responses */
  invoiceType?: InvoiceType;
  /** Highest overdue reminder step sent for it (tracker item 36), or null */
  dunningStep?: number | null;
}

export interface ARAgingCustomerRow {
  customerId: string;
  customerName: string;
  buckets: ARAgingBuckets;
  totalOutstanding: string;
  unappliedCredit: string;
  netBalance: string;
  invoices: ARAgingInvoiceRow[];
}

export interface ARAgingReport {
  asOfDate: string;
  customers: ARAgingCustomerRow[];
  totals: ARAgingBuckets & { totalOutstanding: string; unappliedCredit: string; netBalance: string };
}

export interface RevenueReportQuery extends ReportDateRange {
  customerId?: string;
}

export interface RevenueByItemTypeRow {
  itemType: string;
  /** Net of discounts */
  revenue: string;
  discounts?: string;
  tax: string;
  lineCount: number;
}

export interface RevenueByCustomerRow {
  customerId: string;
  customerName: string;
  /** Net of discounts */
  revenue: string;
  discounts?: string;
  tax: string;
  invoiceCount: number;
}

export interface RevenueReport {
  startDate?: string;
  endDate?: string;
  /** Net revenue: grossRevenue - totalDiscounts */
  totalRevenue: string;
  grossRevenue?: string;
  totalDiscounts?: string;
  totalTax: string;
  byItemType: RevenueByItemTypeRow[];
  byCustomer: RevenueByCustomerRow[];
}

export interface JobReportQuery extends ReportDateRange, ReportPagination {
  status?: string;
  propertyId?: string;
}

export interface JobStatusCountRow {
  status: string;
  count: number;
}

export interface JobByPropertyRow {
  propertyId: string;
  address: string;
  count: number;
}

export interface JobReportRow {
  id: string;
  status: string;
  scheduledDate: string | null;
  completedDate?: string;
  customerId: string;
  customerName: string;
  propertyId: string;
  propertyAddress: string;
  assignedTo: string | null; // user id
}

export interface JobReport {
  startDate?: string;
  endDate?: string;
  byStatus: JobStatusCountRow[];
  byProperty: JobByPropertyRow[];
  data: JobReportRow[];
  pagination: PaginationInfo;
}

export interface PaymentReportQuery extends ReportDateRange, ReportPagination {
  customerId?: string;
  method?: string;
}

export interface PaymentByMethodRow {
  method: string;
  total: string;
  count: number;
}

export interface PaymentReportRow {
  id: string;
  paymentDate: string;
  amount: string;
  paymentMethod: string;
  status: string;
  customerId: string;
  customerName: string;
  referenceNumber?: string;
}

export interface PaymentReport {
  startDate?: string;
  endDate?: string;
  totalPayments: string;
  byMethod: PaymentByMethodRow[];
  data: PaymentReportRow[];
  pagination: PaginationInfo;
}

export interface CustomerStatementQuery extends ReportDateRange, ReportPagination {}

export type StatementLineType = 'INVOICE' | 'PAYMENT';

export interface CustomerStatementLine {
  type: StatementLineType;
  date: string;
  referenceId: string;
  referenceNumber: string;
  description: string;
  /** For INVOICE lines: STANDARD or FINANCE_CHARGE (a late fee); null for payments */
  invoiceType?: InvoiceType | null;
  charge: string;
  credit: string;
  runningBalance: string;
}

// ============ Finance Charge Types ============
// Mirror backend finance-charges.types.ts (tracker item 28).

/**
 * Why a customer is not charged:
 * - ALREADY_ASSESSED: already charged for this period (a cancelled charge still counts)
 * - CREDIT_COVERS_BALANCE: unapplied payment credit covers the overdue balance
 * - BELOW_ONE_CENT: the charge rounds to 0.00 (tiny base, no minimum)
 * - NOTHING_OVERDUE: a requested customer has no overdue balance
 * - ERROR: assessing this customer failed; the others were still assessed
 */
export type FinanceChargeSkipReason =
  | 'ALREADY_ASSESSED'
  | 'CREDIT_COVERS_BALANCE'
  | 'BELOW_ONE_CENT'
  | 'NOTHING_OVERDUE'
  | 'ERROR';

// ============ Flat Contract Billing Types ============
// Mirror backend flat-contract-billing.types.ts (tracker item 34).

/**
 * - DUE: not billed yet; billable.
 * - ALREADY_BILLED: the period has an invoice (a cancelled one still counts).
 * - OVERLAPS_BILLED_PERIOD: the billing period changed and this period
 *   overlaps one already billed, so it is never billed.
 */
export type FlatContractPeriodStatus = 'DUE' | 'ALREADY_BILLED' | 'OVERLAPS_BILLED_PERIOD';

export type FlatContractSkipReason =
  | 'ALREADY_BILLED'
  | 'OVERLAPS_BILLED_PERIOD'
  | 'NOT_A_DUE_PERIOD'
  | 'SCHEDULE_NOT_ACTIVE'
  | 'NOT_FLAT_CONTRACT'
  | 'ERROR';

export interface FlatContractPreviewRow {
  scheduleId: string;
  customerId: string;
  customerName: string;
  billingCustomerId: string;
  billingCustomerName: string;
  propertyId: string;
  propertyAddress: string;
  billingFrequency: FlatContractBillingFrequency;
  billingInterval: number;
  periodStartDate: string;
  periodEndDate: string;
  /** The period containing today (false = a missed earlier period) */
  isCurrent: boolean;
  amount: string;
  /** 4dp fraction (0 for a tax-exempt customer) */
  taxRate: string;
  tax: string;
  total: string;
  status: FlatContractPeriodStatus;
  billable: boolean;
  note: string | null;
  invoice: { invoiceId: string; invoiceNumber: string; status: string } | null;
}

/** GET /flat-contract-billing/preview */
export interface FlatContractPreview {
  asOfDate: string;
  mode: FlatContractBillingMode | string;
  rows: FlatContractPreviewRow[];
  dueCount: number;
  currentDueCount: number;
  totalDue: string;
  truncatedScheduleIds: string[];
}

export interface FlatContractBillItem {
  scheduleId: string;
  periodStartDate: string;
}

/** POST /flat-contract-billing/bill body. Omitted items = every current due period. */
export interface BillFlatContractsRequest {
  items?: FlatContractBillItem[];
}

export interface FlatContractBilled {
  scheduleId: string;
  customerName: string;
  billingCustomerName: string;
  periodStartDate: string;
  periodEndDate: string;
  invoiceId: string;
  invoiceNumber: string;
  amount: string;
  tax: string;
  total: string;
}

export interface FlatContractSkipped {
  scheduleId: string;
  customerName: string | null;
  periodStartDate: string;
  reason: FlatContractSkipReason;
  message: string;
}

export interface BillFlatContractsResult {
  /** Invoice date of every invoice created (the run date) */
  invoiceDate: string;
  billed: FlatContractBilled[];
  skipped: FlatContractSkipped[];
  totalBilled: string;
}

export interface FinanceChargePreviewQuery {
  /** YYYY-MM-DD, not in the future; server defaults to today (UTC) */
  asOfDate?: string;
}

/** POST /finance-charges/assess body */
export interface AssessFinanceChargesRequest {
  asOfDate?: string;
  /** Only these customers; omit for every chargeable customer. Never send []. */
  customerIds?: string[];
}

/** An overdue invoice in a preview row. */
export interface FinanceChargePreviewInvoice {
  invoiceId: string;
  invoiceNumber: string;
  dueDate: string;
  daysOverdue: number;
  remainingBalance: string;
}

/** One customer with an overdue balance as of the date. */
export interface FinanceChargePreviewRow {
  customerId: string;
  customerName: string;
  /** Sum of the overdue invoices' remaining balances */
  overdueBalance: string;
  /** Unapplied payment credit netted off the overdue balance */
  unappliedCredit: string;
  /** max(0, overdueBalance - unappliedCredit): what the rate applies to */
  overdueBase: string;
  /** The charge assess would create ("0" when skipped) */
  charge: string;
  /** False when the customer would be skipped (see skipReason) */
  chargeable: boolean;
  skipReason: FinanceChargeSkipReason | null;
  note: string | null;
  invoices: FinanceChargePreviewInvoice[];
}

export interface FinanceChargeSettingsSnapshot {
  /** Fraction per period, 4dp */
  rate: string;
  graceDays: number;
  /** Money, 2dp */
  minAmount: string;
}

/** GET /finance-charges/preview */
export interface FinanceChargePreview {
  asOfDate: string;
  /** YYYY-MM: one charge per customer per period */
  periodKey: string;
  /** False when the finance charge rate is 0 (rows is then empty) */
  enabled: boolean;
  settings: FinanceChargeSettingsSnapshot;
  rows: FinanceChargePreviewRow[];
  /** Sum of the chargeable rows' charges */
  totalCharge: string;
  chargeableCount: number;
}

/** A charge that was created. */
export interface FinanceChargeAssessed {
  customerId: string;
  customerName: string;
  invoiceId: string;
  invoiceNumber: string;
  overdueBase: string;
  amount: string;
}

/** A customer that was not charged. */
export interface FinanceChargeSkipped {
  customerId: string;
  /** null for a requested customer with nothing overdue */
  customerName: string | null;
  reason: FinanceChargeSkipReason;
  message: string;
}

/** POST /finance-charges/assess */
export interface AssessFinanceChargesResult {
  asOfDate: string;
  periodKey: string;
  /** null when nobody was chargeable (nothing was written) */
  runId: string | null;
  assessed: FinanceChargeAssessed[];
  skipped: FinanceChargeSkipped[];
  totalAssessed: string;
}

// ============ Recurring Job Types ============
// Mirror backend recurring-jobs.types.ts as the controller actually returns it
// (tracker item 29). All dates are YYYY-MM-DD strings in UTC semantics.

export const RECURRING_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'] as const;
export type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number];

export const RECURRING_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'CANCELLED'] as const;
export type RecurringScheduleStatus = (typeof RECURRING_SCHEDULE_STATUSES)[number];

/**
 * PER_VISIT: every generated visit job is invoiced. FLAT_CONTRACT: one invoice
 * per billing period for flatContractAmount; the visit jobs are covered by it.
 */
export type RecurringBillingModel = 'PER_VISIT' | 'FLAT_CONTRACT';

/** A flat contract's billing period, independent of the visit pattern. */
export const FLAT_CONTRACT_BILLING_FREQUENCIES = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'] as const;
export type FlatContractBillingFrequency = (typeof FLAT_CONTRACT_BILLING_FREQUENCIES)[number];

/**
 * WEEKLY: { daysOfWeek: [0-6] } (0 = Sunday).
 * MONTHLY: { dayOfMonth: 1-31 } or { ordinal: 1-4 | -1, dayOfWeek: 0-6 } (-1 = last).
 */
export interface RecurringFrequencyDetails {
  daysOfWeek?: number[];
  dayOfMonth?: number;
  ordinal?: number;
  dayOfWeek?: number;
}

export interface RecurringSchedule {
  id: string;
  tenantId: string;
  propertyId: string;
  property: { id: string; address: string; city: string; state: string };
  customerId: string;
  customer: { id: string; name: string };
  templateJobId: string | null;
  /** `{ id: '', status: '', scheduledDate: null }` when the template job is gone. */
  templateJob: {
    id: string;
    status: string;
    scheduledDate: string | null;
    /** The visits' time fields, copied to every generated job (tracker item 40) */
    scheduledStartTime?: string | null;
    estimatedDurationMinutes?: number | null;
    durationSource?: string;
    arrivalWindowMinutes?: number | null;
  };
  frequency: RecurringFrequency | string;
  interval: number;
  /** null when nothing is stored */
  frequencyDetails?: RecurringFrequencyDetails | null;
  startDate: string;
  endDate?: string;
  /** Latest occurrence a job was generated for */
  lastGeneratedDate?: string;
  /**
   * Generation marker: where the next run starts walking (the start date, the
   * day after the last generated occurrence, or a failed occurrence to retry).
   * Not necessarily a visit date.
   */
  nextScheduledDate?: string;
  skippedDates: string[];
  notes?: string | null;
  status: RecurringScheduleStatus | string;
  billingModel: RecurringBillingModel | string;
  /** FLAT_CONTRACT: pre-tax amount per billing period (2dp); null for PER_VISIT */
  flatContractAmount: string | null;
  billingFrequency: FlatContractBillingFrequency | null;
  billingInterval: number;
  /** FLAT_CONTRACT: billing periods ending before this date are never billed */
  billingStartDate: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A billed period of a flat contract and its invoice. */
export interface RecurringBillingPeriod {
  periodStartDate: string;
  periodEndDate: string;
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: InvoiceStatus | string;
  /** Pre-tax amount billed (2dp) */
  amount: string;
  trigger: 'MANUAL' | 'AUTOMATIC' | string;
  createdAt: string;
}

/** GET /recurring-jobs/:id: the schedule plus the next 5 dates (no job ids). */
export interface RecurringScheduleDetail extends RecurringSchedule {
  /** SKIPPED covers both skipped dates and dates that already have an occurrence. */
  nextOccurrences: { date: string; status: 'PENDING' | 'SKIPPED' | string }[];
  /** FLAT_CONTRACT: start of the next period still to bill; null for PER_VISIT or once ended */
  nextBillingDate: string | null;
  /** Billed periods, newest first (kept after switching back to PER_VISIT) */
  billingPeriods: RecurringBillingPeriod[];
}

/** GET /recurring-jobs/:id/preview */
export interface RecurringPreview {
  scheduleId: string;
  occurrences: {
    date: string;
    willGenerate: boolean;
    /** 'Date is skipped' | 'Occurrence already exists' */
    reason?: string;
  }[];
}

/** POST /recurring-jobs/:id/generate */
export interface RecurringGenerationResult {
  scheduleId: string;
  generatedCount: number;
  failedCount: number;
  nextScheduledDate?: string;
  /** One per failed occurrence (e.g. a CHART line that could not be priced) */
  errors?: { date: string; reason: string }[];
}

/** Contract terms (FLAT_CONTRACT only; PER_VISIT rejects them). */
export interface RecurringBillingTerms {
  /** Default PER_VISIT */
  billingModel?: RecurringBillingModel;
  /** Required for FLAT_CONTRACT: > 0, at most 2 decimals */
  flatContractAmount?: string | null;
  /** Required for FLAT_CONTRACT */
  billingFrequency?: FlatContractBillingFrequency | null;
  /** Every N billing periods (default 1) */
  billingInterval?: number;
}

/** POST /recurring-jobs body. */
export interface CreateRecurringScheduleRequest extends RecurringBillingTerms {
  propertyId: string;
  /** A DRAFT job with an assignee and at least one line item */
  templateJobId: string;
  frequency: RecurringFrequency;
  interval?: number;
  frequencyDetails?: RecurringFrequencyDetails;
  startDate: string;
  endDate?: string;
  skippedDates?: string[];
  notes?: string;
}

/**
 * PATCH /recurring-jobs/:id: the only fields the backend applies. Billing
 * terms are validated against the merged schedule; switching to
 * FLAT_CONTRACT bills from the current period on.
 */
export interface UpdateRecurringScheduleRequest extends RecurringBillingTerms {
  status?: RecurringScheduleStatus;
  frequency?: RecurringFrequency;
  interval?: number;
  frequencyDetails?: RecurringFrequencyDetails;
  startDate?: string;
  /** null or '' clears it */
  endDate?: string | null;
  notes?: string;
}

export interface RecurringScheduleListFilters {
  status?: RecurringScheduleStatus | '';
  customerId?: string;
  propertyId?: string;
}

export interface CustomerStatementReport {
  customerId: string;
  customerName: string;
  startDate?: string;
  endDate?: string;
  openingBalance: string;
  closingBalance: string;
  lines: CustomerStatementLine[];
  pagination: PaginationInfo;
}

// ============ Audit Log Types ============

// Mirrors backend/src/modules/audit/audit.types.ts (GET /api/audit-logs, ADMIN only).

/**
 * Known audit actions (backend AUDIT_ACTIONS). The viewer's action select is
 * filled from GET /audit-logs/actions, not from this list; it is here for the
 * mock client and the label-coverage test.
 */
export const AUDIT_ACTIONS = [
  'user.create',
  'user.update',
  'user.deactivate',
  'user.reactivate',
  'user.reset_password',
  'user.role_change',
  'user.self_password_change',
  'customer.lead_source_change',
  'price_chart.create',
  'price_chart.update',
  'price_chart.tiers_replace',
  'price_chart.delete',
  'checklist_template.create',
  'checklist_template.update',
  'checklist_template.deactivate',
  'checklist_template.delete',
  'job_checklist.apply',
  'job_checklist.remove_group',
  'job_checklist.step_add',
  'job_checklist.step_update',
  'job_checklist.step_delete',
  'job_checklist.step_complete',
  'job_checklist.step_uncomplete',
  'attachment.upload',
  'attachment.update',
  'attachment.delete',
  'company_settings.update',
  'invoice.send',
  'invoice.mark_paid',
  'invoice.cancel',
  'payment.void',
  'payment.credit_apply',
  'payment.credit_unapply',
  'gl.account_create',
  'gl.account_update',
  'gl.journal_entry_manual',
  'gl.journal_entry_reverse',
  'discount.create',
  'discount.update',
  'discount.delete',
  'tax_rate.create',
  'tax_rate.update',
  'tax_rate.delete',
  'finance_charge.assess',
  'flat_contract.bill',
  'auto_invoice.create',
  'dunning.notice',
  'dunning.settings_update',
  'inventory.receive',
  'inventory.adjust',
  'notification.send',
  'notification.retry',
  'notification.cancel',
  'notification.template_update',
  'notification.provider_update',
  'notification.provider_verify',
  'job.schedule',
  'job.start',
  'job.stop',
  'job.finish',
  'job.signature',
  'job.time_entry.create',
  'job.time_entry.update',
  'job.time_entry.delete',
  'job.tech_note',
  'work_order.prepare',
  'work_order.print',
  'work_order.confirm',
  'online_payments.connect',
  'online_payments.disconnect',
  'online_payments.revoked',
  'online_payments.location',
  'online_payments.token_refresh',
  'online_payment.charge',
  'online_payment.refund',
  'online_payment.reconcile',
  'payment_method.add',
  'payment_method.remove',
  'payment_method.default',
  'autopay.enable',
  'autopay.disable',
  'portal.register',
  'portal.verify_email',
  'portal.auto_link',
  'portal.invite',
  'portal.link',
  'portal.unlink',
  'portal.disable',
  'portal.decline',
  'portal.profile_update',
  'invoice.portal_code_regenerate',
  'portal.payment_method_add',
  'portal.payment_method_remove',
  'portal.autopay_consent',
  'portal.service_request',
  'service_request.convert',
  'service_request.decline',
  'portal.reschedule_request',
  'portal.reschedule_cancel',
  'reschedule.approve',
  'reschedule.decline',
  // Estimates (tracker item 39)
  'estimate.create',
  'estimate.update',
  'estimate.send',
  'estimate.resend',
  'estimate.approve',
  'estimate.decline',
  'estimate.cancel',
  'estimate.convert',
  // Customer satisfaction surveys (tracker item 69)
  'survey.settings_update',
  'survey.send',
  'survey.resend',
  // Portal rollout campaigns (tracker item 81)
  'portal_rollout.create',
  'portal_rollout.start',
  'portal_rollout.pause',
  'portal_rollout.resume',
  'portal_rollout.cancel',
  'portal_rollout.send',
  'portal_rollout.resend',
  'portal_rollout.activate',
  'data.import',
  'data.export',
  // Conversation inbox (tracker item 56)
  'conversation.create',
  'conversation.reply',
  'conversation.note',
  'conversation.update',
  'conversation.assign_customer',
  'conversation.discard',
  'portal.message',
  'property.geocode_manual',
  'geocoding.retry_failed',
  // MFA / TOTP (tracker item 63)
  'mfa.enroll',
  'mfa.disable',
  'mfa.reset',
  'mfa.recovery_codes_regenerate',
  'mfa.recovery_code_used',
  'mfa.lockout',
  // Guided setup checklist (tracker item 75)
  'setup.skip',
  'setup.unskip',
  'setup.mark_done',
  'setup.unmark_done',
  'setup.dismiss',
  'setup.restore',
  'setup.reminder',
  // Industry starter packs and recurring presets (tracker item 76)
  'starter.apply',
  'starter.remove',
  'starter.keep',
  'recurring_preset.create',
  'recurring_preset.update',
  'recurring_preset.delete',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** One audit row (backend AuditLogResponse). */
export interface AuditLogEntry {
  id: string;
  actorUserId: string;
  /** Snapshot of the actor's email when the action happened */
  actorEmail: string;
  /** An AuditAction; typed as string so actions newer than this client still render */
  action: string;
  /** Set only when the target is a user */
  targetUserId: string | null;
  targetEmail: string | null;
  /**
   * Parsed JSON, or null when nothing was recorded or the stored text did not
   * parse. Non-user targets are in `details.target = { type, id, label }`.
   */
  details: unknown;
  createdAt: string;
}

/** GET /audit-logs filters (all optional, AND-ed; blank values are not sent). */
export interface AuditLogListFilters {
  actorUserId?: string;
  /** Exact match; must be a known action (400 INVALID_FILTER otherwise) */
  action?: string;
  targetUserId?: string;
  /** Inclusive UTC YYYY-MM-DD on createdAt */
  startDate?: string;
  /** Inclusive UTC YYYY-MM-DD on createdAt */
  endDate?: string;
}

// ============ Inventory Types ============

// Mirrors backend/src/modules/items/item.types.ts (/api/inventory, tracker
// item 30). Quantities and amounts are 2-place decimal strings ("10.00");
// journal line amounts keep the GL format ("40").

/** INVOICE: issue on invoice create / restore on cancel. MANUAL: receipt or count adjustment. */
export type InventoryReferenceType = 'INVOICE' | 'MANUAL';

/** POST /inventory/receive body. */
export interface ReceiveStockRequest {
  itemId: string;
  /** > 0, at most 2 decimals */
  quantity: string;
  /** Per-unit cost of this receipt (> 0 to post, 2dp); omitted = the item's unitCost */
  unitCost?: string;
  /** Account credited (e.g. 2000 Accounts Payable, 1000 Cash); postable, not 1300 */
  offsetAccountId: string;
  /** YYYY-MM-DD */
  adjustmentDate: string;
  memo?: string;
  /** Also set the item's unitCost to this receipt's unitCost (needs unitCost) */
  updateItemCost?: boolean;
}

/** POST /inventory/adjust body. Exactly one of newQuantity / quantityChange. */
export interface AdjustStockRequest {
  itemId: string;
  /** The counted quantity (>= 0 unless allowNegative) */
  newQuantity?: string;
  /** The signed change (negative = shrink), non-zero */
  quantityChange?: string;
  /** Required, at most 500 characters */
  reason: string;
  /** YYYY-MM-DD */
  adjustmentDate: string;
  /** Required to leave the item with a negative quantity on hand */
  allowNegative?: boolean;
}

/** Journal entry summary returned with a manual stock change. */
export interface InventoryJournalEntrySummary {
  id: string;
  entryDate: string;
  memo: string | null;
  lines: Array<{ glAccountId: string; debit: string; credit: string }>;
}

/** Response of POST /inventory/receive and /inventory/adjust. */
export interface InventoryChangeResponse {
  itemId: string;
  itemName: string;
  /** Signed change applied (receive: always positive) */
  qtyChange: string;
  quantityOnHand: string;
  /** The item's unitCost after the change (null if it has none) */
  unitCost: string | null;
  /** Value posted to 1300, signed (negative for a shrink); "0.00" when nothing was posted */
  value: string;
  /** The posted entry, or null for a quantity-only change */
  journalEntry: InventoryJournalEntrySummary | null;
}

/** One GET /inventory/adjustments row. */
export interface InventoryAdjustment {
  id: string;
  itemId: string;
  itemName: string;
  /** YYYY-MM-DD */
  adjustmentDate: string;
  qtyChange: string;
  referenceType: InventoryReferenceType | null;
  /** INVOICE: invoice id. MANUAL: journal entry id, or null if nothing was posted */
  referenceId: string | null;
  memo: string | null;
  createdAt: string;
}

/** GET /inventory/adjustments filters (blank values are not sent). */
export interface InventoryAdjustmentListFilters {
  itemId?: string;
}

/** One GET /inventory/valuation item. */
export interface InventoryValuationItem {
  itemId: string;
  name: string;
  unitOfMeasure: string;
  quantityOnHand: string;
  unitCost: string | null;
  /** round2(quantityOnHand x unitCost); null when the item has no unitCost */
  extendedValue: string | null;
  /** Soft-deleted item still holding stock (listed so the total reconciles) */
  deleted: boolean;
  /** Low-stock threshold; null = none. Optional only for older fixtures. */
  reorderPoint?: string | null;
  /** Active item at or below its reorder point (never for a deleted item). Optional only for older fixtures. */
  lowStock?: boolean;
}

/** GET /inventory/valuation query. */
export interface InventoryValuationFilters {
  /** Only the low-stock rows (the totals and lowStockCount still cover every item). */
  lowStock?: boolean;
}

/** GET /inventory/valuation (ADMIN, OFFICE_MANAGER). */
export interface InventoryValuation {
  items: InventoryValuationItem[];
  totalValue: string;
  /** Items with stock but no unitCost (not valued) */
  unvaluedItemCount: number;
  /** The tenant's 1300 Inventory - Materials account and its balance (debit - credit); null if missing */
  glAccount: { id: string; accountNumber: string; accountName: string; balance: string } | null;
  /** totalValue - GL balance (signed; "0.00" when they reconcile) */
  difference: string;
  /** Low-stock rows across every item (independent of the lowStock filter). Optional only for older fixtures. */
  lowStockCount?: number;
}

// ============ Pagination Types ============

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: PaginationInfo;
  message?: string;
}

// ============ API Response Envelope ============

export interface APIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  code?: string;
}

export interface APIError {
  success: false;
  message: string;
  code: string;
}

// ============ Tenant Types ============

export interface Tenant {
  id: string;
  name: string;
  displayName: string;
  slug: string;
}


// ============ Notification Types (tracker item 35) ============
// Mirrors backend/src/modules/notifications/notifications.types.ts. Provider
// neutral: the settings form is generated from each adapter's configSchema.

export const OUTBOX_STATUSES = ['PENDING', 'SENDING', 'SENT', 'FAILED', 'SKIPPED'] as const;
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];
export type DeliveryStatus = 'DELIVERED' | 'BOUNCED' | 'COMPLAINED' | 'FAILED';
export type ProviderConfigStatus = 'UNVERIFIED' | 'VERIFIED' | 'FAILED';

export interface NotificationTemplate {
  eventKey: string;
  channel: string;
  label: string;
  description: string;
  transactional: boolean;
  variables: { name: string; description: string }[];
  subject: string | null;
  body: string;
  isActive: boolean;
  updatedAt: string | null;
}

export interface UpdateNotificationTemplateRequest {
  subject?: string;
  body?: string;
  isActive?: boolean;
}

export interface NotificationTemplatePreview {
  subject: string | null;
  /** Plain-text part, as it would be sent */
  body: string;
  /** HTML part: the complete email document (fixed layout around the escaped message); null for non-email channels */
  html: string | null;
}

export interface OutboxMessage {
  id: string;
  customerId: string | null;
  customerName: string | null;
  eventKey: string;
  channel: string;
  toAddress: string | null;
  subject: string | null;
  body: string;
  status: OutboxStatus;
  skipReason: string | null;
  skipReasonText: string | null;
  attempts: number;
  nextAttemptAt: string;
  lastError: string | null;
  sentAt: string | null;
  deliveryStatus: DeliveryStatus | null;
  deliveryUpdatedAt: string | null;
  sourceType: string | null;
  sourceId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface OutboxListFilters {
  customerId?: string;
  status?: OutboxStatus | '';
  eventKey?: string;
  sourceType?: string;
  sourceId?: string;
  startDate?: string;
  endDate?: string;
}

export interface NotificationSendResult {
  message: OutboxMessage;
  /** true when the same send was already queued (double-click) */
  deduplicated: boolean;
}

/** Day-of customer messages about one job (tracker item 43). */
export type DayOfEventKey = 'JOB_ON_MY_WAY' | 'JOB_COMPLETED';

/** One day-of message as the job screens show it (`toAddress` is null for a TECHNICIAN). */
export interface DayOfMessageSummary {
  id: string;
  eventKey: string;
  status: OutboxStatus;
  skipReason: string | null;
  skipReasonText: string | null;
  toAddress: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface DayOfEventState {
  eventKey: DayOfEventKey;
  label: string;
  /** The company's notifications and this message type are both on */
  enabled: boolean;
  /** The signed-in user may send it for this job now */
  canSend: boolean;
  unavailableReason: string | null;
  /** If sent now it would not go out, for this reason (no email, opted out, ...) */
  wouldSkip: string | null;
  wouldSkipText: string | null;
  lastMessage: DayOfMessageSummary | null;
}

/** GET /jobs/:id/customer-notifications */
export interface JobCustomerNotifications {
  jobId: string;
  notificationsEnabled: boolean;
  events: DayOfEventState[];
  /** The job's day-of messages, newest first; null for a TECHNICIAN */
  messages: DayOfMessageSummary[] | null;
}

/** POST /jobs/:id/customer-notifications */
export interface SendJobCustomerNotificationResult {
  message: DayOfMessageSummary;
  /** true when the same send was already queued (double-click, or a technician's repeat tap) */
  deduplicated: boolean;
}

export interface ProviderConfigField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'select';
  required: boolean;
  secret: boolean;
  help: string | null;
  options: { value: string; label: string }[] | null;
}

export interface NotificationProviderDescriptor {
  id: string;
  channel: string;
  displayName: string;
  setupHelp: string | null;
  configSchema: ProviderConfigField[];
}

export interface NotificationProviderConfig {
  channel: string;
  provider: string;
  settings: Record<string, string>;
  credentialsHint: string | null;
  hasCredentials: boolean;
  status: ProviderConfigStatus;
  lastVerifiedAt: string | null;
  lastError: string | null;
  updatedAt: string;
}

/** Secret fields are only sent when changed; null clears an optional one. */
export interface SaveNotificationProviderRequest {
  channel: 'EMAIL';
  provider: string;
  settings: Record<string, string>;
  credentials?: Record<string, string | null>;
}

export interface VerifyNotificationProviderResult {
  config: NotificationProviderConfig;
  ok: boolean;
  message: string | null;
}

export interface NotificationSettings {
  enabled: boolean;
  fromName: string | null;
  effectiveFromName: string;
  replyTo: string | null;
  providerStatus: ProviderConfigStatus | null;
  /** 'log': the server only logs messages (development); 'live': real sends */
  deliveryMode: 'log' | 'live';
}

export interface UpdateNotificationSettingsRequest {
  enabled?: boolean;
  fromName?: string | null;
}

export interface PublicLetterhead {
  companyName: string;
  logoUrl: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  footerNote: string | null;
}

export interface PublicInvoiceView {
  company: PublicLetterhead;
  customerName: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  periodStartDate: string | null;
  periodEndDate: string | null;
  status: string;
  lines: { description: string; quantity: string; unitPrice: string; amount: string; discount: string; tax: string }[];
  subtotal: string;
  totalDiscount: string;
  totalTax: string;
  total: string;
  amountPaid: string;
  balanceDue: string;
}

export interface PublicStatementView {
  company: PublicLetterhead;
  customerName: string;
  statementDate: string;
  closingBalance: string;
  lines: { date: string; type: string; reference: string; description: string; charge: string; credit: string; runningBalance: string }[];
}

// ============ Auto-invoicing (tracker item 37) ============
// Mirror backend auto-invoicing.types.ts.

export type AutoInvoiceSkipReason =
  | 'NOT_COMPLETED'
  | 'ALREADY_INVOICED'
  | 'CANCELLED'
  | 'COVERED_BY_FLAT_CONTRACT'
  | 'CUSTOMER_EXCLUDED'
  | 'ON_HOLD'
  | 'NO_LINE_ITEMS'
  | 'ZERO_TOTAL'
  | 'BEFORE_START_DATE'
  | 'NO_COMPLETED_DATE'
  | 'WAITING_FOR_MONTH_END';

export interface AutoInvoiceJobRef {
  jobId: string;
  completedDate: string | null;
  propertyAddress: string;
  subtotal: string;
  tax: string;
}

export interface AutoInvoicePreviewGroup {
  /** `job:<jobId>` (PER_VISIT) or `month:<billingCustomerId>:<customerId>:<YYYY-MM>` (CONSOLIDATED) */
  key: string;
  billingMethod: BillingMethod;
  billingCustomerId: string;
  billingCustomerName: string;
  customerId: string;
  customerName: string;
  periodStartDate: string | null;
  periodEndDate: string | null;
  jobs: AutoInvoiceJobRef[];
  subtotal: string;
  tax: string;
  total: string;
}

export interface AutoInvoiceSkippedJob extends AutoInvoiceJobRef {
  customerId: string;
  customerName: string;
  billingCustomerId: string;
  billingCustomerName: string;
  reason: AutoInvoiceSkipReason;
  message: string;
}

/** GET /auto-invoicing/preview */
export interface AutoInvoicePreview {
  asOfDate: string;
  mode: AutoInvoicingMode | string;
  startDate: string | null;
  autoSend: boolean;
  nightlyHour: number;
  groups: AutoInvoicePreviewGroup[];
  skipped: AutoInvoiceSkippedJob[];
  total: string;
}

/** POST /auto-invoicing/run body. Omitted groups = every group in the preview. */
export interface RunAutoInvoicingRequest {
  groups?: string[];
}

export interface AutoInvoiceGroupResult {
  key: string;
  billingCustomerId: string;
  billingCustomerName: string;
  jobIds: string[];
  status: 'CREATED' | 'SKIPPED' | 'FAILED';
  invoiceId: string | null;
  invoiceNumber: string | null;
  invoiceStatus: string | null;
  total: string | null;
  reason: string | null;
  message: string | null;
  emailStatus: string | null;
  sendError: string | null;
}

export type AutoInvoiceTrigger = 'ON_COMPLETION' | 'NIGHTLY' | 'MANUAL';

export interface AutoInvoiceRunResult {
  runId: string | null;
  trigger: AutoInvoiceTrigger;
  runDate: string;
  results: AutoInvoiceGroupResult[];
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  notRunReason?: string;
}

/** GET /auto-invoicing/runs */
export interface AutoInvoiceRunSummary {
  id: string;
  trigger: AutoInvoiceTrigger;
  runDate: string;
  startedAt: string;
  finishedAt: string | null;
  groupCount: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  createdBy: string | null;
  error: string | null;
  items: {
    jobId: string;
    status: 'CREATED' | 'SKIPPED' | 'FAILED' | 'RELEASED';
    invoiceId: string | null;
    invoiceNumber: string | null;
    reason: string | null;
    message: string | null;
  }[];
}

// ============ Automated dunning (tracker item 36) ============
// Mirror backend dunning.types.ts.

/** A step of the reminder schedule. */
export interface DunningStep {
  id: string;
  /** 1-based order by days ("Step 1") */
  position: number;
  daysOverdue: number;
  /** The step's template (Settings > Notifications), DUNNING_1..DUNNING_6 */
  eventKey: string;
  templateLabel: string;
  templateActive: boolean;
  isActive: boolean;
}

/** GET/PUT /dunning/steps */
export interface DunningSteps {
  steps: DunningStep[];
  maxSteps: number;
  availableEventKeys: string[];
}

/** PUT /dunning/steps body: the whole schedule in ascending days order (no eventKey = a new step). */
export interface ReplaceDunningStepsRequest {
  steps: { eventKey?: string; daysOverdue: number; isActive: boolean }[];
}

export interface DunningInvoiceRef {
  invoiceId: string;
  invoiceNumber: string;
  invoiceType: string;
  dueDate: string;
  daysOverdue: number;
  balance: string;
  stepReached: number | null;
  listed: boolean;
  excludedReason: 'ON_HOLD' | 'BEFORE_START_DATE' | 'BELOW_STEP' | null;
  newNotice: boolean;
}

export interface DunningPreviewRow {
  customerId: string;
  customerName: string;
  willSend: boolean;
  action: 'CONTACT' | 'SKIP';
  reason: string | null;
  message: string | null;
  step: { id: string; position: number; daysOverdue: number; eventKey: string } | null;
  totalOverdue: string;
  overdueCount: number;
  oldestDaysOverdue: number;
  lastRemindedDate: string | null;
  nextReminderDate: string | null;
  invoices: DunningInvoiceRef[];
}

/** GET /dunning/preview */
export interface DunningPreview {
  asOfDate: string;
  mode: DunningMode | string;
  startDate: string | null;
  hour: number;
  minDaysBetween: number;
  customers: DunningPreviewRow[];
  skipped: DunningPreviewRow[];
  totalOverdue: string;
}

/** POST /dunning/run body. Omitted customerIds = every customer the preview contacts. */
export interface RunDunningRequest {
  customerIds?: string[];
}

export interface DunningCustomerResult {
  customerId: string;
  customerName: string;
  status: 'QUEUED' | 'SKIPPED' | 'FAILED';
  stepPosition: number | null;
  eventKey: string | null;
  invoiceIds: string[];
  invoiceNumbers: string[];
  totalOverdue: string | null;
  outboxId: string | null;
  reason: string | null;
  message: string | null;
}

export type DunningTrigger = 'NIGHTLY' | 'MANUAL';

export interface DunningRunResult {
  runId: string | null;
  trigger: DunningTrigger;
  runDate: string;
  results: DunningCustomerResult[];
  queuedCount: number;
  skippedCount: number;
  failedCount: number;
  notRunReason?: string;
}

/** GET /dunning/runs */
export interface DunningRunSummary {
  id: string;
  trigger: DunningTrigger;
  runDate: string;
  startedAt: string;
  finishedAt: string | null;
  customerCount: number;
  queuedCount: number;
  skippedCount: number;
  failedCount: number;
  createdBy: string | null;
  error: string | null;
  results: DunningCustomerResult[];
}

/** GET /dunning/invoices/:invoiceId/notices */
export interface DunningNotice {
  id: string;
  status: 'QUEUED' | 'SKIPPED' | 'SUPERSEDED';
  reason: string | null;
  stepId: string | null;
  stepPosition: number;
  stepDaysOverdue: number;
  eventKey: string;
  daysOverdue: number;
  balance: string;
  runId: string;
  runDate: string;
  outboxId: string | null;
  createdAt: string;
}

// ============================================================
// ONLINE PAYMENTS AND AUTOPAY (tracker item 38)
// ============================================================
// Provider-neutral: the processor is a string id; its browser configuration
// is an opaque record the payment form adapter interprets.

export type ProcessorEnvironment = 'SANDBOX' | 'PRODUCTION';
export type ProcessorConnectionStatus = 'CONNECTED' | 'REVOKED' | 'ERROR';
export type OnlinePaymentSource = 'PUBLIC_LINK' | 'AUTOPAY' | 'STAFF' | 'PORTAL';
export type OnlinePaymentAttemptStatus = 'PENDING' | 'CHARGED' | 'RECORDED' | 'DECLINED' | 'ERROR' | 'RECORD_FAILED';
export const ONLINE_PAYMENT_ATTEMPT_STATUSES: OnlinePaymentAttemptStatus[] = ['PENDING', 'CHARGED', 'RECORDED', 'DECLINED', 'ERROR', 'RECORD_FAILED'];
export type PaymentMethodStatus = 'ACTIVE' | 'EXPIRED' | 'REMOVED';

/** Non-secret browser configuration of the processor's card form (provider + environment + adapter-specific keys). */
export type PaymentClientConfig = { provider: string; environment: ProcessorEnvironment } & Record<string, string>;

export interface ProcessorConnection {
  connected: boolean;
  provider: string | null;
  providerName: string | null;
  environment: ProcessorEnvironment | null;
  status: ProcessorConnectionStatus | null;
  accountId: string | null;
  accountName: string | null;
  locationId: string | null;
  locationName: string | null;
  locations: { id: string; name: string }[];
  accessTokenExpiresAt: string | null;
  lastRefreshedAt: string | null;
  lastError: string | null;
  connectedAt: string | null;
  /** Non-secret browser config of the card form (staff add-card dialog); null unless connected and usable */
  clientConfig: PaymentClientConfig | null;
  /** The processor this server connects to (null: none configured, or no secret key) */
  availableProvider: { id: string; name: string; environment: ProcessorEnvironment } | null;
}

export interface OnlinePaymentSettings {
  enabled: boolean;
  minAmount: string;
  autopayRetryDays: number[];
  autopayMaxFailures: number;
  connectionStatus: ProcessorConnectionStatus | null;
  autopayWorkerEnabled: boolean;
  autopayDryRun: boolean;
  /** The consent wording staff show the customer before turning autopay on for them */
  autopayConsent: { version: string; text: string };
}

export interface UpdateOnlinePaymentSettingsRequest {
  enabled?: boolean;
  minAmount?: string;
  autopayRetryDays?: number[];
  autopayMaxFailures?: number;
}

export interface PublicPayConfig {
  available: boolean;
  unavailableReason: string | null;
  invoiceNumber: string;
  balanceDue: string;
  minAmount: string;
  currency: string;
  clientConfig: PaymentClientConfig | null;
  cardsOnFile: boolean;
  autopayConsent: { version: string; text: string } | null;
}

export interface PublicPayRequest {
  sourceId: string;
  amount: string;
  saveCard?: boolean;
  autopayConsent?: boolean;
}

export interface PublicPayResult {
  status: 'PAID' | 'PROCESSING' | 'DECLINED';
  amount: string;
  message: string;
  receiptUrl: string | null;
  card: { brand: string | null; last4: string | null } | null;
  balanceDue: string;
  cardSaved: boolean;
  autopayEnabled: boolean;
}

export interface CustomerPaymentMethod {
  id: string;
  customerId: string;
  provider: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  autopayEnabled: boolean;
  autopayConsentAt: string | null;
  autopayConsentVersion: string | null;
  /** CUSTOMER (pay link) or STAFF */
  autopayConsentBy: string | null;
  status: PaymentMethodStatus;
  createdAt: string;
}

export interface AutopayConsentInput {
  accepted: true;
  version: string;
}

export interface OnlinePaymentAttempt {
  id: string;
  invoiceId: string | null;
  invoiceNumber: string | null;
  customerId: string;
  customerName: string;
  paymentMethodId: string | null;
  provider: string;
  amount: string;
  source: OnlinePaymentSource;
  status: OnlinePaymentAttemptStatus;
  processorPaymentId: string | null;
  paymentId: string | null;
  paymentStatus: string | null;
  paymentProcessorStatus: string | null;
  processingFee: string | null;
  receiptUrl: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  failureCode: string | null;
  failureMessage: string | null;
  attemptNo: number | null;
  needsReview: boolean;
  reviewNote: string | null;
  needsAttention: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OnlinePaymentAttemptSummary {
  recordFailed: number;
  outcomeUnknown: number;
  needsReview: number;
  refundedAtProcessor: number;
  voidPending: number;
}

export interface OnlinePaymentAttemptFilters {
  status?: OnlinePaymentAttemptStatus | '';
  source?: OnlinePaymentSource | '';
  customerId?: string;
  invoiceId?: string;
  needsAttention?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface OnlinePaymentAttemptList extends PaginatedResponse<OnlinePaymentAttempt> {
  summary: OnlinePaymentAttemptSummary;
}

export interface StaffChargeRequest {
  paymentMethodId: string;
  amount?: string;
  idempotencyKey?: string;
}

export interface ChargeOutcome {
  attempt: OnlinePaymentAttempt;
  deduplicated: boolean;
}

export interface RefundResult {
  payment: Payment;
  processorRefundId: string | null;
}

// ============ Work Order Types (tracker item 58) ============

// Mirrors backend/src/modules/work-orders/work-orders.types.ts
// (/api/work-order-batches, ADMIN / OFFICE_MANAGER only).

/** Filter value for jobs with no route / no assignee. */
export const WORK_ORDER_UNROUTED = 'UNROUTED';
export const WORK_ORDER_UNASSIGNED = 'UNASSIGNED';
/** Most copies of one type per job (backend CHECK). */
export const MAX_WORK_ORDER_COPIES = 10;

export type WorkOrderEligibleStatus = 'SCHEDULED' | 'WORK_ORDER_GENERATED';
export type WorkOrderBatchStatus = 'PREPARED' | 'PARTIALLY_CONFIRMED' | 'CONFIRMED';
export type WorkOrderCopyType = 'SERVICE' | 'BILLING';

export interface WorkOrderCandidate {
  id: string;
  status: string;
  customer: { id: string; name: string };
  property: { id: string; address: string; city: string | null; state: string | null };
  scheduledDate: string;
  scheduledStartTime: string | null;
  routeId: string | null;
  /** null = Unrouted */
  routeName: string | null;
  assignedTo: string | null;
  /** null = Unassigned */
  assignedToEmail: string | null;
  lineCount: number;
  /** Preparation refuses a job with no work items */
  hasWorkItems: boolean;
  /** A confirmed batch printed it before (later prints are labeled REPRINT) */
  previouslyConfirmed: boolean;
  /** Service bundles (tracker item 85): the visit's enrollment chip data; null outside an enrollment */
  enrollmentId?: string | null;
  enrollmentStep?: number | null;
  enrollmentStepCount?: number | null;
  bundleName?: string | null;
  updatedAt: string;
}

export interface WorkOrderFacet {
  /** Route / user id, or UNROUTED / UNASSIGNED */
  id: string;
  label: string;
  count: number;
}

export interface WorkOrderCandidateQuery {
  /** Omitted = tomorrow in the company timezone (the response says which) */
  date?: string;
  routeId?: string;
  assignedTo?: string;
  status?: WorkOrderEligibleStatus;
  search?: string;
  cursor?: string;
  limit?: number;
}

export interface WorkOrderCandidatePage {
  date: string;
  today: string;
  timezone: string;
  data: WorkOrderCandidate[];
  total: number;
  dayTotal: number;
  nextCursor: string | null;
  routes: WorkOrderFacet[];
  technicians: WorkOrderFacet[];
}

export interface WorkOrderSelection {
  date: string;
  scope: 'DAY' | 'TECHNICIAN';
  assignedTo: string | null;
  total: number;
  jobs: WorkOrderCandidate[];
}

export interface WorkOrderBatchSummary {
  id: string;
  scheduledDate: string;
  status: WorkOrderBatchStatus;
  preparedBy: string;
  preparedByEmail: string;
  jobCount: number;
  confirmedJobCount: number;
  unconfirmedJobCount: number;
  serviceCopyCount: number;
  billingCopyCount: number;
  printAttempts: number;
  lastPrintedAt: string | null;
  lastConfirmedAt: string | null;
  lastConfirmedByEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export type WorkOrderConfirmBlock = 'JOB_MISSING' | 'STATUS_CHANGED' | 'DATE_CHANGED';

export interface WorkOrderBatchMember {
  jobId: string;
  position: number;
  serviceCopies: number;
  billingCopies: number;
  originalStatus: string;
  printCount: number;
  lastPrintedAt: string | null;
  confirmedAt: string | null;
  confirmedByEmail: string | null;
  printedVersion: string | null;
  transitioned: boolean;
  /** Prior confirmed print history: label REPRINT */
  reprint: boolean;
  routeName: string | null;
  /** The Job's CURRENT details; null when it no longer exists */
  job: Job | null;
  /**
   * The Job's CURRENT checklist (tracker item 44), printed on both copy
   * types: no groups when it has no steps; null when the job no longer exists.
   */
  checklist: JobChecklist | null;
  /** Why an unconfirmed member cannot be confirmed now (null = it can) */
  confirmBlock: WorkOrderConfirmBlock | null;
}

export interface WorkOrderBatchDetail extends WorkOrderBatchSummary {
  members: WorkOrderBatchMember[];
  company: CompanySettings;
}

export interface WorkOrderBatchList {
  data: WorkOrderBatchSummary[];
  pagination: PaginationInfo;
}

export interface WorkOrderBatchListQuery {
  date?: string;
  status?: WorkOrderBatchStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PrepareWorkOrderBatchRequest {
  date: string;
  jobs: Array<{ id: string; serviceCopies: number; billingCopies: number }>;
}

export interface ConfirmWorkOrderJobsResult {
  batch: WorkOrderBatchSummary;
  newlyGenerated: string[];
  alreadyGenerated: string[];
  alreadyConfirmed: string[];
  stillUnconfirmed: string[];
}

export interface RecordWorkOrderPrintResult {
  batch: WorkOrderBatchSummary;
  reprintJobIds: string[];
}

/** One job that blocked a preparation or confirmation (409 `details.problems`). */
export interface WorkOrderJobProblem {
  jobId: string;
  reason: string;
  message: string;
  label?: string;
  currentStatus?: string;
  currentUpdatedAt?: string;
}

// ============ Checklists (tracker item 44) ============

/** Limits enforced by the backend (checklists.types.ts). */
export const CHECKLIST_TEMPLATE_NAME_MAX = 255;
export const CHECKLIST_STEP_TEXT_MAX = 500;
export const CHECKLIST_TEMPLATE_MAX_STEPS = 100;
/** Group a hand-made job step joins when no group name is given. */
export const JOB_CHECKLIST_DEFAULT_GROUP = 'Additional steps';

export interface ChecklistTemplateStep {
  id: string;
  text: string;
  required: boolean;
  sortOrder: number;
}

/** GET /checklist-templates[/:id]. Steps are ordered by sortOrder. */
export interface ChecklistTemplate {
  id: string;
  name: string;
  isActive: boolean;
  stepCount: number;
  /** Items linked to the template (soft-deleted items included) */
  itemCount: number;
  /** Job checklist steps copied from the template */
  jobStepCount: number;
  /** itemCount > 0 or jobStepCount > 0: DELETE deactivates instead of deleting */
  inUse: boolean;
  steps: ChecklistTemplateStep[];
  createdAt: string;
  updatedAt: string;
}

/** One step in a create / replace request. Array position is the step order. */
export interface ChecklistTemplateStepInput {
  text: string;
  /** Defaults to false */
  required?: boolean;
}

/** POST /checklist-templates. Name unique per tenant, inactive included (409 DUPLICATE_CHECKLIST_TEMPLATE). */
export interface CreateChecklistTemplateRequest {
  name: string;
  /** Defaults to true */
  isActive?: boolean;
  /** 1-100 steps */
  steps: ChecklistTemplateStepInput[];
}

/** PUT /checklist-templates/:id: replaces the name and the whole step set (step ids change). */
export interface ReplaceChecklistTemplateRequest {
  name: string;
  /** Omitted = unchanged */
  isActive?: boolean;
  steps: ChecklistTemplateStepInput[];
}

export type ChecklistTemplateDeleteOutcome = 'DELETED' | 'DEACTIVATED';

/** DELETE /checklist-templates/:id: deleted, or deactivated when in use. */
export interface ChecklistTemplateDeleteResult {
  result: ChecklistTemplateDeleteOutcome;
  /** The deactivated template; null when it was deleted */
  template: ChecklistTemplate | null;
}

export interface ChecklistTemplateListFilters {
  /** true = active only, false = inactive only; omitted = all */
  active?: boolean;
}

/** Completed / total counts for one job. Required steps warn only. */
export interface JobChecklistProgress {
  total: number;
  completed: number;
  /** Required steps not yet ticked */
  requiredIncomplete: number;
}

export interface JobChecklistStep {
  id: string;
  jobId: string;
  sourceTemplateId: string | null;
  groupName: string;
  text: string;
  sortOrder: number;
  required: boolean;
  completed: boolean;
  completedAt: string | null;
  /** User id of whoever ticked the step (not resolved to a name) */
  completedById: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Steps sharing a template (or, for hand-made steps, a group name), in step order. */
export interface JobChecklistGroup {
  sourceTemplateId: string | null;
  groupName: string;
  total: number;
  completed: number;
  steps: JobChecklistStep[];
}

/** GET /jobs/:id/checklist */
export interface JobChecklist extends JobChecklistProgress {
  jobId: string;
  /** Ordered by each group's first step */
  groups: JobChecklistGroup[];
}

/** POST /jobs/:id/checklist/steps */
export interface AddJobChecklistStepRequest {
  /** 1-500 characters (trimmed) */
  text: string;
  required?: boolean;
  /** Joins an existing group of that name; blank = 'Additional steps' */
  groupName?: string;
  /** Whole number 0-1,000,000; default after every step */
  sortOrder?: number;
}

/** PUT /jobs/:id/checklist/steps/:stepId: at least one field. */
export interface UpdateJobChecklistStepRequest {
  text?: string;
  required?: boolean;
  sortOrder?: number;
}

/** POST /jobs/:id/checklist/apply-template */
export interface ApplyJobChecklistTemplateResult {
  /** false when the job already had that template's group (nothing changed) */
  applied: boolean;
  checklist: JobChecklist;
}


// ============ Attachments (tracker item 47) ============

/** The record an attachment belongs to (exactly one). */
export type AttachmentParentType = 'CUSTOMER' | 'PROPERTY' | 'JOB';

/** Backend AttachmentResponse. The storage key is never sent. */
export interface Attachment {
  id: string;
  parentType: AttachmentParentType;
  parentId: string;
  /** Display filename (cleaned by the server); downloads should use this, not Content-Disposition */
  filename: string;
  /** Canonical type detected from the bytes */
  contentType: string;
  sizeBytes: number;
  description: string | null;
  /** JPEG, PNG, WebP or HEIC */
  isImage: boolean;
  /** JPEG, PNG, WebP: served inline, so the browser can preview it (HEIC and everything else download only) */
  inline: boolean;
  uploadedById: string;
  /** Snapshot of the uploader's email at upload time */
  uploadedByEmail: string;
  createdAt: string;
  updatedAt: string;
}

/** POST /{customers|properties|jobs}/:id/attachments options (multipart `file` + `description`). */
export interface UploadAttachmentOptions {
  /** Optional, at most 1000 characters (trimmed; blank = none) */
  description?: string;
  /** Upload progress, 0..1 */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** PATCH /attachments/:id: at least one field. */
export interface UpdateAttachmentRequest {
  /** Must keep an extension of the same file type (else 400 FILE_EXTENSION_CHANGE) */
  filename?: string;
  /** null or '' clears it */
  description?: string | null;
}

export interface DownloadAttachmentOptions {
  /** `?download=true`: the server sends Content-Disposition: attachment even for images */
  download?: boolean;
}

// ============ Technician view (tracker item 42, phase 2) ============
// Mirrors backend modules/technician. No prices, totals or balances anywhere.

export type TimeEntrySource = 'TIMER' | 'MANUAL';

export interface TechnicianTimeEntry {
  id: string;
  jobId: string;
  userId: string;
  userEmail: string;
  startedAt: string;
  endedAt: string | null;
  running: boolean;
  /** Whole seconds between start and end; null while running */
  durationSeconds: number | null;
  source: TimeEntrySource;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export interface TechnicianWorkItem {
  id: string;
  itemId: string;
  name: string;
  description: string | null;
  quantity: string;
}

/** One job card (today list and single job). */
export interface TechnicianJobCard {
  id: string;
  status: JobStatus;
  assignedTo: string | null;
  scheduledDate: string | null;
  /** Local "HH:MM" in the company timezone */
  scheduledStartTime: string | null;
  /** Start plus the duration ("HH:MM"); null without a start time */
  scheduledEndTime: string | null;
  estimatedDurationMinutes: number | null;
  arrivalWindowMinutes: number | null;
  completedDate: string | null;
  route: { id: string; name: string } | null;
  customer: { id: string; name: string; phone: string | null };
  property: { id: string; address: string; city: string; state: string; zip: string };
  notes: string | null;
  workItems: TechnicianWorkItem[];
  checklist: JobChecklistProgress;
  /** Timers running on this job right now (any user) */
  openTimeEntries: { id: string; userId: string; startedAt: string }[];
  /** Service bundles (tracker item 85): the visit's enrollment chip data; null outside an enrollment */
  enrollmentId?: string | null;
  enrollmentStep?: number | null;
  enrollmentStepCount?: number | null;
  bundleName?: string | null;
  updatedAt: string;
}

export interface TechnicianToday {
  /** The day listed (YYYY-MM-DD) */
  date: string;
  /** Today in the company timezone */
  today: string;
  timezone: string;
  user: { id: string; email: string };
  jobs: TechnicianJobCard[];
  /** Open jobs from before today, oldest first */
  unfinished: TechnicianJobCard[];
  unfinishedTruncated: boolean;
  /** The user's running timer, wherever it is */
  runningEntry: TechnicianTimeEntry | null;
}

export interface TechnicianTodayQuery {
  /** YYYY-MM-DD; default today in the company timezone */
  date?: string;
  /** Office roles only: list another user's jobs */
  userId?: string;
}

export interface TechnicianJobState {
  id: string;
  status: JobStatus;
  completedDate: string | null;
}

export interface StartTechnicianJobResult {
  job: TechnicianJobState;
  entry: TechnicianTimeEntry;
  /** The caller's timer was already running on this job; nothing changed */
  alreadyRunning: boolean;
  offSchedule: boolean;
}

export interface StopTechnicianJobResult {
  job: TechnicianJobState;
  entry: TechnicianTimeEntry;
}

export interface FinishTechnicianJobResult {
  job: TechnicianJobState;
  closedEntries: TechnicianTimeEntry[];
  /** The signature recorded with Finish; null when none was sent */
  signature: JobSignature | null;
  /** Required checklist steps left unticked (warn, never block) */
  requiredIncomplete: number;
}

/** 409 details of TIMER_RUNNING_ELSEWHERE. */
export interface TimerRunningElsewhereDetails {
  jobId?: string;
  entryId?: string;
  startedAt?: string;
}

/** 409 details of NOT_SCHEDULED_TODAY. */
export interface NotScheduledTodayDetails {
  scheduledDate: string | null;
  today: string;
}

// ============ Technician view (tracker item 42, phase 3) ============

/** Limits mirrored from the backend (technician.types.ts). */
export const SIGNATURE_MAX_BYTES = 1024 * 1024;
export const SIGNATURE_MAX_DIMENSION = 4096;
export const SIGNER_NAME_MAX = 200;
export const TECH_NOTE_MAX = 2000;

/** A customer signature on a job (PNG kept by the server; never carries a storage key). */
export interface JobSignature {
  id: string;
  jobId: string;
  signerName: string;
  contentType: string;
  sizeBytes: number;
  signedAt: string;
  capturedById: string;
  capturedByEmail: string;
  /** False once a newer signature replaced it */
  current: boolean;
  supersededAt: string | null;
  supersededById: string | null;
}

/** GET /jobs/:id/signatures (newest first; `current` is null when never signed). */
export interface JobSignatureList {
  jobId: string;
  current: JobSignature | null;
  signatures: JobSignature[];
}

/** A drawn signature ready to send: the PNG and the printed name. */
export interface TechnicianSignatureInput {
  blob: Blob;
  signerName: string;
}

export interface FinishTechnicianJobOptions {
  /** Optional; never required to finish */
  signature?: TechnicianSignatureInput;
}

/** POST /jobs/:id/tech-notes */
export interface TechnicianNoteResult {
  jobId: string;
  /** The stamped line that was appended */
  line: string;
  /** The job's whole notes text after the append */
  notes: string;
}

// ============ Customer portal (tracker item 45, phase 3) ============

// Mirrors backend/src/modules/portal/portal.types.ts (/api/portal/*). A portal
// user (role CUSTOMER_PORTAL) is linked to exactly ONE customer; no response
// carries a customer id, a processor id, a job price or an internal note.

/** POST /portal/register (public): no password; the customer chooses it on the verification page. */
export interface PortalRegisterRequest {
  tenantSlug: string;
  email: string;
  name: string;
  phone: string;
  /** XXXX-XXXX from an invoice, any case / spacing */
  invoiceCode?: string;
  /** Proof (any two of address, phoneLast4, invoiceNumber / invoiceAmount, when there is no code) */
  address?: string;
  phoneLast4?: string;
  invoiceNumber?: string;
  invoiceAmount?: string;
}

/** The one answer to a registration, whatever happened. */
export interface PortalRegisterResult {
  registered: true;
  message: string;
}

/** POST /portal/verify-email and /portal/invite/accept and /portal/password-reset/confirm. */
export interface PortalTokenPasswordRequest {
  token: string;
  password: string;
}

export interface PortalVerifyResult {
  verified: true;
}

export interface PortalOkResult {
  ok: true;
}

export interface PortalResetRequestResult {
  requested: true;
  message: string;
}

export type PortalRelation = 'OWNED' | 'BILLED_TO_YOU';

export interface PortalProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  relation: PortalRelation;
}

export type PortalAccessRequestStatus = 'PENDING' | 'LINKED' | 'DECLINED';

/** GET /portal/me */
export interface PortalMe {
  user: { id: string; email: string; name: string; firstName: string | null; lastName: string | null; phone: string | null };
  /** LINKED sees the customer's data; UNLINKED is waiting for (or without) a customer match */
  status: 'LINKED' | 'UNLINKED';
  customerName: string | null;
  companyName: string;
  /** Hours of notice a reschedule request needs (the company's setting) */
  rescheduleLeadHours: number;
  accessRequest: { status: PortalAccessRequestStatus; createdAt: string } | null;
  properties: PortalProperty[];
}

/** An address as the portal shows it. */
export interface PortalAddress {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

export interface PortalInvoiceSummary {
  id: string;
  invoiceNumber: string;
  status: string;
  invoiceType: string;
  invoiceDate: string;
  dueDate: string;
  periodStartDate: string | null;
  periodEndDate: string | null;
  /** Incl. tax */
  total: string;
  amountPaid: string;
  balanceDue: string;
  /**
   * The single property the invoice's jobs (or a flat contract period) were at;
   * null when it has none (a finance charge) or spans several. It may be a property
   * owned by someone else but billed to this customer.
   */
  property: PortalAddress | null;
}

export interface PortalInvoiceLine {
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  amount: string;
  discount: string;
  tax: string;
}

export interface PortalInvoiceDetail extends PortalInvoiceSummary {
  subtotal: string;
  totalDiscount: string;
  totalTax: string;
  lines: PortalInvoiceLine[];
  discounts: { name: string; type: string; amount: string }[];
  /** kind CREDIT: earlier credit applied by the office (tracker item 55); date = the day it was applied */
  payments: { kind?: 'PAYMENT' | 'CREDIT'; date: string; method: string; cardBrand: string | null; cardLast4: string | null; amount: string }[];
}

export type PortalJobStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export interface PortalJob {
  id: string;
  status: PortalJobStatus;
  scheduledDate: string | null;
  /** Local "HH:MM" */
  scheduledStartTime: string | null;
  arrivalWindowMinutes: number | null;
  completedDate: string | null;
  property: { id: string; address: string; city: string; state: string; zip: string };
  /** The technician's first name, or "Your technician" */
  technician: string;
  /** Name and quantity only, never a price */
  items: { name: string; quantity: string }[];
}

export interface PortalStatementLine {
  date: string;
  type: string;
  reference: string;
  description: string;
  charge: string;
  credit: string;
  runningBalance: string;
}

export interface PortalStatement {
  customerName: string;
  statementDate: string;
  openingBalance: string;
  closingBalance: string;
  lines: PortalStatementLine[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface PortalProfile {
  email: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  notificationOptOut: boolean;
  customerName: string;
}

/** PATCH /portal/profile (the email cannot be changed here). */
export interface PortalProfileUpdate {
  name?: string;
  phone?: string | null;
  notificationOptOut?: boolean;
}

export interface PortalPage<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface PortalListQuery {
  /** One of the customer's properties; unknown / not theirs is 404 */
  propertyId?: string;
  page?: number;
  limit?: number;
}

/** POST /portal/invoices/:id/pay: like the public link, but the consent is the explicit object. */
export interface PortalPayRequest {
  sourceId: string;
  amount: string;
  saveCard?: boolean;
  autopayConsent?: AutopayConsentInput;
}

/** A card on file as the portal shows it (no processor ids, no customer id). */
export interface PortalPaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  isDefault: boolean;
  autopayEnabled: boolean;
  autopayConsentAt: string | null;
  autopayConsentVersion: string | null;
  status: PaymentMethodStatus;
}

/** GET /portal/payment-methods */
export interface PortalCards {
  /** Cards can be added and autopay turned on */
  available: boolean;
  unavailableReason: string | null;
  clientConfig: PaymentClientConfig | null;
  autopayConsent: { version: string; text: string } | null;
  methods: PortalPaymentMethod[];
}

export interface PortalAddCardRequest {
  sourceId: string;
  makeDefault?: boolean;
  autopayConsent?: AutopayConsentInput;
}

export interface PortalAutopayRequest {
  enabled: boolean;
  /** Required (explicit, current version) every time autopay is turned ON */
  autopayConsent?: AutopayConsentInput;
}

// ============ Customer portal, phase 4: requests (tracker item 45) ============

// Mirrors backend modules/service-requests and modules/reschedule-requests and
// the portal's office routes (customers/:id/portal, portal-access-requests).

export type ServiceRequestStatus = 'NEW' | 'CONVERTED' | 'DECLINED';
export type ServiceRequestSource = 'PORTAL' | 'WEB_FORM';
export type ServiceRequestTimeOfDay = 'ANY' | 'MORNING' | 'AFTERNOON' | 'EVENING';

export const SERVICE_REQUEST_TIMES_OF_DAY: readonly ServiceRequestTimeOfDay[] = ['ANY', 'MORNING', 'AFTERNOON', 'EVENING'];

/** GET /portal/requestable-items */
export interface PortalRequestableItem {
  id: string;
  name: string;
  description: string | null;
}

/** POST /portal/service-requests: a property (own) OR a typed address, and a service or notes. */
export interface PortalServiceRequestInput {
  requestedItemId?: string;
  propertyId?: string;
  serviceAddress?: string;
  /** YYYY-MM-DD, at most 5, not before today */
  preferredDates?: string[];
  preferredTimeOfDay?: ServiceRequestTimeOfDay;
  notes?: string;
  contactName?: string;
  contactPhone?: string;
}

/** The customer's view of a request: status, what was asked, the office's decline reason. No staff ids, no job id. */
export interface PortalServiceRequest {
  id: string;
  status: ServiceRequestStatus;
  requestedItem: string | null;
  property: PortalAddress | null;
  serviceAddress: string | null;
  preferredDates: string[];
  preferredTimeOfDay: ServiceRequestTimeOfDay | null;
  notes: string | null;
  createdAt: string;
  decidedAt: string | null;
  declineReason: string | null;
}

export type RescheduleRequestStatus = 'OPEN' | 'APPROVED' | 'DECLINED' | 'AUTO_APPROVED' | 'CANCELLED';

/** POST /portal/jobs/:id/reschedule-requests */
export interface PortalRescheduleInput {
  /** YYYY-MM-DD, after today in the company timezone */
  requestedDate: string;
  /** HH:MM */
  requestedStartTime?: string;
  /** Free text such as "mornings" (informational, 100 characters) */
  requestedWindow?: string;
  /** 500 characters */
  reason?: string;
}

/** The customer's view of a reschedule request. */
export interface PortalRescheduleRequest {
  id: string;
  jobId: string;
  status: RescheduleRequestStatus;
  requestedDate: string;
  requestedStartTime: string | null;
  requestedWindow: string | null;
  reason: string | null;
  /** What was applied, once approved */
  approvedDate: string | null;
  approvedStartTime: string | null;
  /** The office's message when it declined */
  declineReason: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface RequestPageQuery {
  page?: number;
  limit?: number;
}

/** GET /service-requests (office) */
export interface ServiceRequestListQuery extends RequestPageQuery {
  status?: ServiceRequestStatus;
  source?: ServiceRequestSource;
}

/** The office's view of a service request. */
export interface ServiceRequest {
  id: string;
  source: ServiceRequestSource;
  status: ServiceRequestStatus;
  customer: { id: string; name: string } | null;
  property: PortalAddress | null;
  serviceAddress: string | null;
  requestedItem: { id: string | null; name: string | null } | null;
  preferredDates: string[];
  preferredTimeOfDay: ServiceRequestTimeOfDay | null;
  notes: string | null;
  contact: { name: string; email: string | null; phone: string | null };
  convertedJobId: string | null;
  decidedById: string | null;
  decidedAt: string | null;
  declineReason: string | null;
  createdByUserId: string | null;
  createdAt: string;
}

/** POST /service-requests/:id/convert: customer / property only when the request has none (400 CUSTOMER_REQUIRED / PROPERTY_REQUIRED). */
export interface ConvertServiceRequestInput {
  customerId?: string;
  propertyId?: string;
}

// ============ Web form intake (tracker item 49) ============

/** GET / PUT /service-request-intake (ADMIN only). The key is null until the form is first enabled. */
export interface WebFormIntakeSettings {
  enabled: boolean;
  publicKey: string | null;
  keyCreatedAt: string | null;
}

/** GET /public/web-form/:key/items: the services the public form offers (id and name only). */
export interface WebFormItem {
  id: string;
  name: string;
}

/**
 * POST /public/web-form/:key/service-requests (no sign-in). A name, an email
 * or a phone (at least one), a typed address, and a service or notes.
 * `website` is the hidden honeypot: anything in it and the post is dropped.
 */
export interface WebFormRequestInput {
  contactName: string;
  contactEmail?: string;
  contactPhone?: string;
  serviceAddress: string;
  requestedItemId?: string;
  /** YYYY-MM-DD, at most 5, not before today */
  preferredDates?: string[];
  preferredTimeOfDay?: ServiceRequestTimeOfDay;
  notes?: string;
  website?: string;
}

/** GET /reschedule-requests (office) */
export interface RescheduleRequestListQuery extends RequestPageQuery {
  status?: RescheduleRequestStatus;
}

/** The office's view of a reschedule request. */
export interface RescheduleRequest {
  id: string;
  jobId: string;
  status: RescheduleRequestStatus;
  customer: { id: string; name: string };
  property: PortalAddress;
  jobStatus: string;
  requestedByUserId: string;
  originalDate: string | null;
  originalStartTime: string | null;
  requestedDate: string;
  requestedStartTime: string | null;
  requestedWindow: string | null;
  reason: string | null;
  decidedById: string | null;
  decidedAt: string | null;
  declineReason: string | null;
  approvedDate: string | null;
  approvedStartTime: string | null;
  createdAt: string;
}

/** POST /reschedule-requests/:id/approve */
export interface ApproveRescheduleInput {
  /** Override the requested date (YYYY-MM-DD) */
  scheduledDate?: string;
  /** Override the requested start time (HH:MM); null = no start time */
  scheduledStartTime?: string | null;
  /** Apply even when the dispatch checks report conflicts (else 409 RESCHEDULE_CONFLICTS) */
  overrideConflicts?: boolean;
  /** A note for the customer (emailed, not stored) */
  message?: string;
}

/** GET /portal-access-requests (office) */
export type PortalAccessRequestFilter = PortalAccessRequestStatus | 'ALL';

export interface PortalAccessRequestListQuery extends RequestPageQuery {
  status?: PortalAccessRequestFilter;
}

export type PortalReviewReason =
  | 'NO_EMAIL_MATCH'
  | 'AMBIGUOUS_EMAIL'
  | 'EMAIL_UNUSABLE'
  | 'CUSTOMER_HAS_LOGIN'
  | 'TOO_FEW_PROOF_FIELDS'
  | 'LOGIN_LIMIT'
  | 'CUSTOMER_LOCKED'
  | 'CODE_NOT_USABLE';

/** A verified registration that could not be linked automatically, with what matched whom (never the typed values). */
export interface PortalAccessRequest {
  id: string;
  status: PortalAccessRequestStatus;
  /** Repeated failed matches for one customer */
  flagged: boolean;
  createdAt: string;
  user: { id: string; email: string; name: string; phone: string | null };
  reason: PortalReviewReason | null;
  /** A registration code was typed */
  codeGiven: boolean;
  candidates: {
    customerId: string;
    customerName: string | null;
    matched: { address: boolean; phoneLast4: boolean; invoice: boolean } | null;
  }[];
  decidedAt: string | null;
  linkedCustomerId: string | null;
  declineReason: string | null;
}

/** PATCH /portal-access-requests/:id */
export type PortalAccessRequestDecision =
  | { action: 'link'; customerId: string }
  | { action: 'decline'; reason?: string };

/** A portal login as staff see it on the customer page (GET /customers/:id/portal). */
export interface CustomerPortalLogin {
  userId: string;
  email: string;
  name: string;
  status: 'PENDING_EMAIL' | 'ACTIVE';
  isActive: boolean;
  linkedVia: 'INVITE' | 'INVOICE_CODE' | 'AUTO_EMAIL' | 'STAFF';
  linkedAt: string;
}

/** POST /customers/:id/portal/invite */
export interface PortalInviteInput {
  /** Defaults to the customer's email */
  email?: string;
  name?: string;
}

export interface PortalInviteResult {
  userId: string;
  email: string;
  status: 'PENDING_EMAIL';
  /** QUEUED: the email is on its way; SKIPPED: it could not be sent (see skipReason) */
  delivery: 'QUEUED' | 'SKIPPED';
  skipReason: string | null;
}

// Estimates / quotes (tracker item 39)
export * from './estimateTypes';

// Two-way conversation inbox (tracker item 56)
export * from './conversationTypes';
// Import / export (tracker item 52)
export * from './importExportTypes';

// Global search (tracker item 61)
export * from './searchTypes';
export * from './mfaTypes';

// Guided setup checklist (tracker item 75)
export * from './setupTypes';

// Industry starter packs and recurring presets (tracker item 76)
export * from './starterTypes';

// Staff in-app notification center (tracker item 62)
export * from './staffNotificationTypes';

// Customer satisfaction surveys (tracker item 69)
export * from './surveyTypes';

// Portal rollout campaigns (tracker item 81)
export * from './portalRolloutTypes';
export * from './bundleTypes';
